import { useEffect, useRef, useState, createContext, Children } from 'react';
import { info, error, warning } from '../utils/console';
import { useSelector, useDispatch } from 'react-redux';
import { editStatus, incStatus } from '../redux/state/status';
import { generateChatMode, generateToken } from '../utils/tokenGenerator';
import {
    ChatInterface,
    ReceiveEventInterface,
    ReceiveAuthenticatedEvent,
    SendChasitorMessageEventInterface,
    SendChatEndInterface,
    SendChatHistoryEvent,
    SendChatRatingInterface,
    SendCheckVipEventInterface,
    SendConnectAgentEventInterface,
    SendConsumeMessageEventInterface,
    SendEventInterface,
    SendFormSubmitEventInterface,
    SendHandshakeTokenEvent,
    SendIntentEventInterface,
    SendMessageEventInterface,
    SendReconectSseEventInterface,
    SendReconnectAgentEventInterface,
    SendUploadChatEventInterface,
    SendUploadFileEventInterface,
    SendVerifyReconnectionEventInterface,
    SendFileMessageInterface,
    SendMessageReadInterface,
    SendLoginInterface,
    SendBotChatEndInterface,
    SendMessageSeenInterface
} from '../utils/interfaces';
import { useCacheService } from '../hook/useCacheService';
import { useSessionService } from '../hook/useSessionService';
import { setCookies } from '../utils/cookies';
import { addDialog, removeDialog } from '../redux/state/dialog';
import { useTranslation } from 'react-i18next';
import {
    appendSession,
    editSession,
    editSessions
} from '../redux/state/session';
import { format, isToday } from 'date-fns';
import { addChat } from '../redux/state/chat';
import { func, param } from 'ts-interface-checker';
import Swal from 'sweetalert2';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getBrowser, getPlatform } from '../utils/salesforceVisitorDetails';
import { store } from '../redux/store'; 
import { getOS } from '../utils/navigator';
import { shouldShowWebToCaseMaintenance } from '../utils/webToCaseMaintenance';
import { splitChatHistoryTextMessage } from '../utils/chatHistoryMessageParser';
import { is } from 'date-fns/locale';

export const SocketContext = createContext<any>({} as any);

interface Props {
    // session: {
    //     acctId: number;
    //     botId: number;
    //     chatId: string;
    //     chatMode: string;
    //     isCached: boolean;
    //     sessionId: string;
    //     username: string;
    //     isVip: boolean;
    //     tabId: number;
    //     brandId: 'welton' | 'masuta' | 'vpo';
    //     dbProfile: 'mario.staging.1' | 'mario.production';
    // };
    session: any;
    systemMessages: any;
    settings: any;
    socketPaths: string[];
    children: JSX.Element;
    setDebug: any;
    debug: any;
}

declare const window: any;

/** Tries `{nativeAppId}://adjust?frompage=vipchat`, then main-site URL if the app does not open. */
const redirectNativeAppOrMainSite = (
    nativeAppId: unknown,
    mainSiteLoginUrl: string
) => {
    const id =
        nativeAppId !== undefined && nativeAppId !== null
            ? String(nativeAppId).trim()
            : '';
    if (!id) {
        window.location.replace(mainSiteLoginUrl);
        return;
    }
    const appUrl = `${id}://adjust?frompage=vipchat`;
    const fallbackDelayMs = 2000;
    const fallbackTimer = window.setTimeout(() => {
        window.location.replace(mainSiteLoginUrl);
    }, fallbackDelayMs);

    const cancelFallback = () => {
        window.clearTimeout(fallbackTimer);
    };

    window.addEventListener('pagehide', cancelFallback, { once: true });
    document.addEventListener(
        'visibilitychange',
        () => {
            if (document.visibilityState === 'hidden') {
                cancelFallback();
            }
        },
        { once: true }
    );

    window.location.href = appUrl;
};

const waitForSessionRestored = (maxMs = 1500) =>
    new Promise<boolean>((resolve) => {
        const start = Date.now();
        const check = () => {
            if (store.getState().status.isSessionRestored) {
                resolve(true);
                return;
            }
            if (Date.now() - start >= maxMs) {
                resolve(false);
                return;
            }
            setTimeout(check, 50);
        };
        check();
    });

const SocketContextProvider = (props: Props) => {
    const [socket, setSocket] = useState<WebSocket | null>(null);
    const [path, setPath] = useState<string | URL>('null');
    const [chatId, setChatId] = useState('');
    const [chatModeLocal, setChatModeLocal] = useState('bot');
    const [agentName, setAgentName] = useState('');
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const session = useSelector((state: { session: any }) => state.session);
    const sessionService = useSessionService();
    const botIdRequireLanguageSelection = [49, 75, 88, '49', '75', '88'];
    const chatMode = useSelector(
        (state: { session: { chatMode: string } }) => state.session.chatMode
    );
    const isStartBot = useSelector(
        (state: { session: { isStartBot: boolean } }) =>
            state.session.isStartBot
    );
    const isStartAgent = useSelector(
        (state: { session: { isStartAgent: boolean } }) =>
            state.session.isStartAgent
    );
    const isBotToAgent = useSelector(
        (state: { session: { isBotToAgent: boolean } }) =>
            state.session.isBotToAgent
    );
    const respondedToBot = useSelector(
        (state: { session: { respondedToBot: boolean } }) =>
            state.session.respondedToBot
    );
    const respondedToAgent = useSelector(
        (state: { session: { respondedToAgent: boolean } }) =>
            state.session.respondedToAgent
    );
    const chatCountReason = useSelector(
        (state: { session: { chatCountReason: string } }) =>
            state.session.chatCountReason
    );
    const username = useSelector(
        (state: { session: { username: string } }) => state.session.username
    );
    const botButtonColor = useSelector(
        (state: { settings: { botButtonColor: string } }) =>
            state.settings.botButtonColor
    );
    const botName = useSelector(
        (state: { settings: { botName: string } }) =>
            state.settings.botName
    );

    const botLanguage = useSelector(
        (state: { settings: { botLanguage: string } }) =>
            state.settings.botLanguage
    );

    const customValues = useSelector(
        (state: { settings: { customValues: string } }) =>
            state.settings.customValues
    );

    const chats = useSelector((state: { chat: ChatInterface[] }) => state.chat);
    const isChatAlive = useSelector(
        (state: { status: { isChatAlive: boolean } }) =>
            state.status.isChatAlive
    );

    const conversationId = useSelector(
        (state: { session: { conversationId: string } }) =>
            state.session.conversationId
    );

    const accessToken = useSelector(
        (state: { session: { accessToken: string } }) =>
            state.session.accessToken
    );

    const betslipId = useSelector(
        (state: { session: { betslipId: string } }) => state.session.betslipId
    );

    const matchStartDate = useSelector(
        (state: { session: { matchStartDate: string } }) =>
            state.session.matchStartDate
    );

    const vendor = useSelector(
        (state: { session: { vendor: string } }) => state.session.vendor
    );

    const status = useSelector(
        (state: { session: { status: string } }) => state.session.status
    );

    const referrerSite = useSelector(
        (state: { session: { referrerSite: string } }) => state.session.referrerSite
    );

    const queueStatus = useSelector(
        (state: { status: { queueStatus: string } }) => state.status.queueStatus
    );

    const caseNumber = useSelector(
        (state: { session: { caseNumber: string } }) => state.session.caseNumber
    );

    const caseCategory = useSelector(
        (state: { session: { caseCategory: string } }) => state.session.caseCategory
    );

    const refreshWhenBot = useSelector(
        (state: { session: { refreshWhenBot: boolean } }) =>
            state.session.refreshWhenBot
    );

    const laAffinityToken = useSelector(
        (state: { session: { laAffinityToken: string } }) =>
            state.session.laAffinityToken
    );

    const laSessionKey = useSelector(
        (state: { session: { laSessionKey: string } }) =>
            state.session.laSessionKey
    );

    const refreshWhenQueueing = useSelector(
        (state: { session: { refreshWhenQueueing: string } }) =>
            state.session.refreshWhenQueueing
    );

    const queueNumber = useSelector(
        (state: { status: { queueNumber: number } }) => state.status.queueNumber
    );

    const enQueue = useSelector(
        (state: { session: { enQueue: boolean } }) => state.session.enQueue
    );

    const historyTranscriptSent = useSelector(
        (state: { session: { historyTranscriptSent: boolean } }) => state.session.historyTranscriptSent
    );

    const processor = useSelector(
        (state: { settings: { processor: number } }) => state.settings.processor
    );

    const handShakeToken = useSelector(
        (state: { session: { handShakeToken: string } }) => state.session.handShakeToken
    );

    const affiliateCode = useSelector(
        (state: { session: { affiliateCode: string } }) => state.session.affiliateCode
    );

    const waitingAnswerReply = useSelector(
        (state: { session: { waitingAnswerReply: boolean } }) => state.session.waitingAnswerReply
    );

    const retryCacheAttemps = useSelector(
        (state: { session: { retryCacheAttemps: number } }) => state.session.retryCacheAttemps
    );

    const vip = useSelector(
        (state: { session: { isVip: any } }) => state.session.isVip
    );

    const isAuthenticated = useSelector(
        (state: { session: { isAuthenticated: any } }) =>
            state.session.isAuthenticated
    );

    const welcomeMessageSent = useSelector(
        (state: { session: { welcomeMessageSent: boolean } }) =>
            state.session.welcomeMessageSent
    );

    const welcomeUploadAcked = useSelector(
        (state: { session: { welcomeUploadAcked: boolean } }) =>
            state.session.welcomeUploadAcked
    );

    const brandCode = useSelector(
        (state: { settings: { brandCode: string } }) => state.settings.brandCode
    );

    const marketCode = useSelector(
        (state: { settings: { marketCode: string } }) => state.settings.marketCode
    )

    const intentId = useSelector(
        (state: { status: { intentId: string } }) => state.status.intentId
    );

    const replyStatus = useSelector(
        (state: { status: { replyStatus: string } }) => state.status.replyStatus
    );

    const showLiveAgentConnectedMessage = useSelector(
        (state: { status: { showLiveAgentConnectedMessage: boolean } }) => state.status.showLiveAgentConnectedMessage
    );

    const chatTs = useSelector(
        (state: { session: { chatTs: string } }) => state.session.chatTs
    );

    const dataCustomValues = customValues ? JSON.parse(customValues) : {};
    const live_agent_flag = dataCustomValues.hasOwnProperty('live_agent_mode') ? dataCustomValues.live_agent_mode : '0';
    const webToCaseForm = dataCustomValues.hasOwnProperty('web_to_case_form') ? dataCustomValues.web_to_case_form : 'web_to_case';
    const isVipBot = String(dataCustomValues?.is_vip_bot ?? 'false').toLowerCase() === 'true';
    const mainSiteUrl = dataCustomValues.hasOwnProperty('main_site_url') ? dataCustomValues.main_site_url : '';
    const byPassChatTs = String(dataCustomValues?.by_pass_chat_ts ?? 'false').toLowerCase() === 'true';
    const isAffBot = String(dataCustomValues?.is_aff_bot ?? 'false').toLowerCase() === 'true';
    const affiliateAvailabilityPending = useSelector(
        (state: { status: { affiliateAvailabilityPending: boolean } }) =>
            state.status.affiliateAvailabilityPending
    );


    const dispatch = useDispatch();
    const {
        setCache,
        checkCache,
        claimChatId,
        pushCache,
        setCachesByFields,
        setCacheByField,
        getCacheByField,
        getCache,
        popCache,
        refreshCache,
        endSession,
    } = useCacheService();
    const { t } = useTranslation();

    const [timeoutTimer, setTimeoutTimer] = useState<any>({});
    const [liveAgentSession, setLiveAgentSession] = useState({
        affinityToken: '',
        key: '',
    });
    const chasitorInFlightSignatureRef = useRef<string | null>(null);
    const historyTranscriptScheduledRef = useRef(false);

    useEffect(() => {
        historyTranscriptScheduledRef.current = historyTranscriptSent == true;
    }, [historyTranscriptSent]);

    let offlineCount = 0;
    let onlineCount = 0;
    let vipVals = props.session.isVip;
    console.log("vipVals here",vipVals);

    const socketEvent: any = {
        eventType: 'initial',
        botId: props.session.botId,
        acctId: props.session.acctId,
        brandId: props.session.brandId,
        dbProfile: props.session.dbProfile,
    };

    const startSessionChecker = (chatId: string) => {
        // dispatch(appendSession({ name: 'debugMessage', value: 'start-' }));
        clearInterval(timeoutTimer)
        const t = setInterval(async () => {
            console.log("session check", chatId)
            if (chatId) {
                sendConsumeMessageEvent({
                    chatId,
                });
            }
            sendPingEvent(chatId);
            const state = store.getState();
            const currentChatMode = state?.session?.chatMode;
            const currentQueueStatus = state?.status?.queueStatus;
            const isQueueingNow =
                currentChatMode === 'agent' &&
                (
                    currentQueueStatus === 'loading' ||
                    currentQueueStatus === 'queueLoading' ||
                    currentQueueStatus === 'queueing' ||
                    currentQueueStatus === 'queueingDialog'
                );

            if (isQueueingNow) {
                try {
                    await refreshCache(props.session.sessionId);
                } catch (e) {
                    // best effort
                }
            }

            const ended = await checkCache(
                props.session.sessionId,
                props.session.tabId,
                { suspendTimeout: isQueueingNow }
            );

            if (ended) {
                // dispatch(
                //     appendSession({ name: 'debugMessage', value: 'end-' })
                // );
                clearInterval(t);
                stopSessionChecker();
            }
        }, 30000);
        setTimeoutTimer(t);
    };

    const stopSessionChecker = async () => {
        // check
        dispatch(appendSession({ name: 'debugMessage', value: 'stop-' }));
        clearInterval(timeoutTimer);
        setTimeoutTimer({});
    };

    const onOpen = (msg: any) => {
        info(onOpen.name, 'on open!');
    };

    const onMessage = (msg: any) => {
        const event = JSON.parse(msg.data);
        if (Array.isArray(event)) {
            event.map((_event: any, index: number) => {
                socketEventHandler(_event);
            });
        } else {
            socketEventHandler(event);
        }
    };

    const checkBetSlipValue = () => {
        if(betslipId) {
            if (matchStartDate && (vendor && status)) {
                return true;
            } else{
                return false;
            }
        } else {
            return false;
        }
    }

    const isAssignedToAgent = () => {
        if (betslipId) {
            return true;
        }
        else if (caseNumber){
            return true;
        }

        return false;
    }

    const socketEventHandler = (event: any) => {
        console.log('RECEIVE EVENT', event);
        try {
            if (event.eventType === 'pong') {
                // dispatch(appendSession({ name: 'debugMessage', value: 'po-' }));
            }
            if (event.eventType == 'reply') {
                handleReplyEvent(event);
            } else if (event.eventType == 'connected') {
                handleConnectedEvent(event);
            } else if (event.eventType == 'invalidConnected') {
                dispatch(
                    editStatus({
                        name: 'affiliateAvailabilityPending',
                        value: false,
                    })
                );
            // } else if (event.eventType == 'authenticate') {
                // handleAuthenticatedEvent(event);
            } else if (event.eventType == 'liveAgentMessageInserted') {
                handleMessageInsertedEvent(event);
            } else if (event.eventType == 'memberVipStatus') {
                handleMemberVipStatus(event);
            } else if (event.eventType == 'loginStatus') {
                handleLoginStatus(event);
            } else if (event.eventType == 'storedChatHistoriesList') {
                handleStoredChatHistoriesList(event);
            } else if (event.eventType == 'liveAgentRequestSuccess') {
                handleLiveAgentRequestSuccessEvent(event);
            } else if (event.eventType == 'liveAgentRequestFail') {
                handleLiveAgentRequestFailEvent(event);
            } else if (event.eventType == 'liveAgentQueueUpdate') {
                handleLiveAgentQueueUpdateEvent(event);
            } else if (event.eventType == 'liveAgentChatEstablished') {
                handleLiveAgentChatEstablishedEvent(event);
            } else if (event.eventType == 'liveAgentChatMessage') {
                handleLiveAgentChatMessageEvent(event);
            } else if (event.eventType == 'liveAgentChatTransferred') {
                handleLiveAgentChatTransferredEvent(event);
            } else if (event.eventType == 'liveAgentRequestFile') {
                handleLiveAgentFileRequestEvent(event);
            } else if (event.eventType == 'liveAgentChatEnded') {
                handleLiveAgentChatEnded(event);
            } else if (event.eventType == 'terminateStatus') {
                handleTerminateStatus(event);
            } else if (event.eventType == 'reconnectionVerified') {
                handleReconnectionVerified(event);
            } else if (event.eventType == 'eventCacheNotFound'){
                handleEventCacheNotFound(event);
            } else if (event.eventType == 'duplicateAgentSession'){
                handleDuplicateAgentSession(event);
            } else if (event.eventType == 'conversationCreated') {
                handleCreateConversationEvent(event)
            } else if (event.eventType == 'CONVERSATION_MESSAGE') {
                handleReceiveLiveAgentMessageEvent(event)
            } else if (event.eventType == 'fileUpload') {
                handleFileUploadEvent(event)
            } else if (event.eventType == 'reconnectSse') {
                handleReconnectSseEvent(event)
            } else if (event.eventType == 'agentJoined') {
                handleAgentJoinedConversation(event)
            } else if (event.eventType == 'miawRoutingResult'){
                handleLiveAgentRoutingResultEvent(event)
            } else if (event.eventType == 'miawQueuePosition'){
                handleLiveAgentQueuePositionEvent(event)
            } else if (event.eventType == 'uploadFileStatus'){
                handleUploadFileStatusEvent(event)
            }
        } catch (e: any) {
            error(SocketContextProvider.name, e);
        }
    };

    const handleWaitingAnswerReply = async (firstCall: boolean, retryCacheAttemps: number) => {
        // 20 * 5 sec = 100 sec
        let maxRetries = 20 
        let retryCount = retryCacheAttemps + 1
        setCacheByField(props.session.sessionId, 'retryCacheAttemps', retryCount);
        if (retryCount > maxRetries){
            const error_response_chat_json: ChatInterface = {
                owner: 'bot',
                name: props.settings.botName,
                type: 'text',
                message: {
                    content: props.systemMessages.error_response.message,
                },
                displayTime: format(new Date(), 'hh:mmaa'),
                createdBy: new Date().getTime(),
            };
            dispatch(addChat(error_response_chat_json));
            pushCache(
                props.session.sessionId + '_chats',
                JSON.stringify(error_response_chat_json)
            );
            sendUploadChatEvent({
                chatId: chatId,
                speaker: 'Bot',
                speakerName: props.settings.botName,
                datetime: new Date().toJSON(),
                message: props.systemMessages.error_response.message,
            });
            await dispatch(editStatus({ name: 'isBotTyping', value: false }));
            dispatch(editSession({ name: 'waitingAnswerReply', value: false }));
            setCacheByField(props.session.sessionId, 'waitingAnswerReply', false);
            setCacheByField(props.session.sessionId, 'retryCacheAttemps', 0);
        }
        try {
            if (firstCall) {
                await dispatch(editStatus({ name: 'isBotTyping', value: true }));
            }
    
            let waitingAnswerReply = await getCacheByField(props.session.sessionId, 'waitingAnswerReply');
            waitingAnswerReply = JSON.parse(waitingAnswerReply);
            if(!waitingAnswerReply){
                const cache_chats = await popCache(props.session.sessionId + '_chats');
                if (cache_chats.length > chats.length) {
                    const lastChat = cache_chats[cache_chats.length - 1];
                    dispatch(addChat(JSON.parse(lastChat)));
                }
                await dispatch(editStatus({ name: 'isBotTyping', value: false }));
                dispatch(editSession({ name: 'waitingAnswerReply', value: false }));
                setCacheByField(props.session.sessionId, 'waitingAnswerReply', false);
                setCacheByField(props.session.sessionId, 'retryCacheAttemps', 0);
            } else {
                setTimeout(() => handleWaitingAnswerReply(false, retryCount), 5000);
            }
        } catch (error) {
            console.error("Error in handleWaitingAnswerReply:", error);
        }
    };

    const handleDuplicateAgentSession = (event:ReceiveEventInterface) => {
        dispatch(
            editSessions({
                laAffinityToken: event.data.live_agent_info.affinityToken,
                laSessionKey: event.data.live_agent_info.key,
            })
        );
        sendReconnectAgentEvent({
            chatId: props.session.chatId,
            username: props.session.username,
        });
    }

    const handleEventCacheNotFound = (event:ReceiveEventInterface) => {

        if (props.session.laAffinityToken && props.session.laSessionKey){

            const state = store.getState();
            const memberCode = state.session.memberCode;

            sendChatEndEvent({
                chatId: props.session.chatId,
                affinityToken: props.session.laAffinityToken,
                key: props.session.laSessionKey,
                memberCode: memberCode,
                brandCode: brandCode,
                isVipBot: isVipBot,
                isAffBot: isAffBot
            });
            
        }
        endSession(props.session.sessionId);
        window.location.reload();
    }

    const verifyVipChecked = () => {

        console.log("IS VIP CHECK", vipVals, typeof vipVals);
        if (vipVals[1] === "notChecked"){
            console.log("IS VIP UNCHECK HERE");
            let data = customValues ? JSON.parse(customValues) : {};
            const customHandShakeToken = data.handshake_token ? JSON.parse(data.handshake_token) : {};
            const isActive = customHandShakeToken.isActive ? customHandShakeToken.isActive : 'false';
            sendCheckVipEvent({
                chatId: chatId,
                username: username,
                usernameFromUrl: true, //check
                recheckVip: true,
                merchant: data.hasOwnProperty('merchant') ? data.merchant : "",
                market: data.hasOwnProperty('market') ? data.market : "",
                bbuPromoApiUrl: data.hasOwnProperty('bbu_promo_api_url') ? data.bbu_promo_api_url : "",
                bbuPromoDefaultUrl: data.hasOwnProperty('bbu_promo_default_url') ? data.bbu_promo_default_url : "",
                checkVipDetails: data['check_vip_details'] ? data['check_vip_details'] : null,
                handShakeToken: handShakeToken,
                affiliate_code: affiliateCode || '',
                isActive: isActive,
                brandCode: brandCode,
                redisSessionId: props.session.botId.toString()+props.session.sessionId,
                redisUrl : props.session.apiPath,
                marketCode: marketCode,
                isVipBot: isVipBot,
            })
        } else if (vipVals[1] === "checked") {
            console.log("IS VIP CHECK HERE")
        }
    }

    const clearAffiliateAvailabilityPending = () => {
        dispatch(
            editStatus({
                name: 'affiliateAvailabilityPending',
                value: false,
            })
        );
    };

    const handleConnectedEvent = async (event: ReceiveEventInterface) => {
        let data = JSON.parse(customValues);
        let live_agent_mode = data['live_agent_mode'] ? data['live_agent_mode'] : '0'
        if (window.isRN) {
            window.ReactNativeWebView.postMessage(
                JSON.stringify({
                    key: 'CHECK TOKEN',
                    value: props.session.sessionId,
                })
            );
        }
        console.log('connected event', event, props.session);

        if (!props.session.chatId) {
            if (!event.data.validChatTs){
                clearAffiliateAvailabilityPending();
                const baseUrl = (dataCustomValues?.main_site_url || "").replace(/\/$/, "");
                const finalUrl = `${baseUrl}/login?frompage=normalchat`;
                try {
                    endSession(props.session.sessionId);
                } finally {
                    redirectNativeAppOrMainSite(
                        dataCustomValues?.native_app_id,
                        finalUrl
                    );
                }
            }
            const isRestored = await waitForSessionRestored();
            const stateAfterRestore = store.getState();
            const sessionId =
                stateAfterRestore.session.sessionId || props.session.sessionId;
            const tabId =
                stateAfterRestore.session.tabId || props.session.tabId;

            if (sessionId && tabId) {
                await setCacheByField(sessionId, 'tabId', tabId);
            }

            const restoredChatId = stateAfterRestore.session.chatId;
            const socketChatId = event.data?.chatId;
            const preferredChatId =
                isRestored && restoredChatId ? restoredChatId : socketChatId;

            let effectiveChatId = preferredChatId;
            if (sessionId && typeof claimChatId === 'function') {
                try {
                    const claim = await claimChatId(sessionId, preferredChatId);
                    effectiveChatId = claim.chatId || preferredChatId;
                } catch (e) {
                    error(SocketContextProvider.name, 'claimChatId failed', e);
                }
            }

            if (
                socketChatId &&
                effectiveChatId &&
                String(socketChatId) !== String(effectiveChatId)
            ) {
                const extraChatId = String(socketChatId);
                const memberCode = stateAfterRestore.session.memberCode;
                sendBotChatEndEvent({
                    chatId: extraChatId,
                    isAuthenticated: stateAfterRestore.session.isAuthenticated,
                    memberCode: memberCode,
                    brandCode: brandCode,
                });
                sendChatEndEvent({
                    chatId: extraChatId,
                    affinityToken: stateAfterRestore.session.laAffinityToken,
                    key: stateAfterRestore.session.laSessionKey,
                    conversationId: stateAfterRestore.session.conversationId,
                    accessToken: stateAfterRestore.session.accessToken,
                    memberCode: memberCode,
                    brandCode: brandCode,
                    isVipBot: isVipBot,
                    isAffBot: isAffBot
                });
            }

            dispatch(
                editSession({
                    name: 'chatId',
                    value: effectiveChatId,
                })
            );

            dispatch(
                editStatus({ name: 'queueStatus', value: 'loading' })
            );

            setChatId(effectiveChatId);

            // AFCC-131: early Aff Visitor availability before current checkVip / Pre-Chat.
            const agentAvailable = (event.data as any)?.agentAvailable;
            if (agentAvailable === false) {
                // Keep pending true so Pre-Chat stays hidden during W2C/Maintenance.
                runAgentsUnavailableOfflineFallback(String(effectiveChatId || ''));
                return;
            }
            clearAffiliateAvailabilityPending();

            if (props.session.username) {
                let data = customValues ? JSON.parse(customValues) : {};
                const customHandShakeToken = data.handshake_token
                    ? JSON.parse(data.handshake_token)
                    : {};
                const isActive = customHandShakeToken.isActive
                    ? customHandShakeToken.isActive
                    : 'false';
                sendCheckVipEvent({
                    chatId: effectiveChatId,
                    username: props.session.username,
                    usernameFromUrl: true, //check
                    recheckVip: false,
                    merchant: data.hasOwnProperty('merchant') ? data.merchant : '',
                    market: data.hasOwnProperty('market') ? data.market : '',
                    checkVipDetails: data['check_vip_details']
                        ? data['check_vip_details']
                        : null,
                    bbuPromoApiUrl: data.hasOwnProperty('bbu_promo_api_url')
                        ? data.bbu_promo_api_url
                        : '',
                    bbuPromoDefaultUrl: data.hasOwnProperty('bbu_promo_default_url')
                        ? data.bbu_promo_default_url
                        : '',
                    specUsername: props.session.username,
                    handShakeToken: handShakeToken,
                    affiliate_code: affiliateCode || '',
                    isActive: isActive,
                    brandCode: brandCode,
                    redisUrl     : props.session.apiPath,
                    redisSessionId: props.session.botId.toString()+props.session.sessionId,
                    marketCode: marketCode,
                    isVipBot: isVipBot,
                });
            }
            startSessionChecker(effectiveChatId);
        } else {
            clearAffiliateAvailabilityPending();
            if (props.session.sessionId && props.session.tabId) {
                setCacheByField(props.session.sessionId, 'tabId', props.session.tabId);
            }
            setChatId(props.session.chatId);
            const state = store.getState();
            const memberCode = state.session.memberCode;
            if (props.session.chatMode === 'agent') {
                setChatModeLocal('agent');
                const laAffinityToken = await getCacheByField(props.session.sessionId, 'laAffinityToken') // check SF values present in cache
                const conversationId = await getCacheByField(props.session.sessionId, 'conversationId')
                const accessToken = await getCacheByField(props.session.sessionId, 'accessToken')
                const isAuthenticatedStr = await getCacheByField(props.session.sessionId, 'isAuthenticated');
                const isAuthenticated = isAuthenticatedStr === "true";
                if (props.session.isCached && props.session.refreshWhenQueueing == true && live_agent_mode !== '1'){
                    console.log("Refreshed or closed tab during queue page");

                    let logQueueNumber = queueNumber + 1;

                    sendUploadChatEvent({
                        chatId: chatId,
                        speaker: 'Client',
                        speakerName: username,
                        datetime: new Date().toJSON(),
                        message: "Refreshed or closed tab during queue page. Member queue position at " + logQueueNumber.toString(),
                        is_system: true,
                    });

                    dispatch(editStatus({ name: 'queueStatus', value: 'canceled' }));
                    
                    dispatch(
                        addDialog({
                            task: 'ended',
                            type: 'action',
                        })
                    );
                    
                    sendChatEndEvent({
                        chatId: props.session.chatId,
                        affinityToken: props.session.laAffinityToken,
                        key: props.session.laSessionKey,
                        liveAgentFlag: live_agent_mode,
                        conversationId: conversationId,
                        accessToken: accessToken,
                        isAuthenticated: isAuthenticated,
                        memberCode: memberCode,
                        brandCode: brandCode,
                        isVipBot: isVipBot,
                        isAffBot: isAffBot
                    });
                    endSession(props.session.sessionId);
                    window.location.reload();
                    
                } else if ((props.session.isCached && live_agent_mode !== '1') || laAffinityToken) {
                    setChatModeLocal('agent');
                    console.log('reconnect!', props.session.isCached, laAffinityToken) 
                    sendReconnectAgentEvent({
                        chatId: props.session.chatId,
                        username: props.session.username,
                    });
                } else if (props.session.isCached && live_agent_mode == '1') {
                    console.log('sse reconnect', props.session.isCached, conversationId)
                    
                    dispatch(
                        editStatus({
                            name: 'queueStatus',
                            value: queueStatus,
                        })
                    );
                    
                    sendReconnectSseEvent({
                        chatId: props.session.chatId,
                        memberCode: memberCode,
                        brandCode: brandCode,
                        isVipBot: isVipBot,
                        isAffBot: isAffBot
                    });
                } else {
                    verifyVipChecked();
                    dispatch(editSession({ name: 'chatMode', value: 'agent' }));
                    setCacheByField(props.session.sessionId, 'chatMode', 'agent');
                    connectAgent({isVip: vipVals[0] , flag: "handleConnectEvent"});
                }
            } else if (props.session.chatMode === 'bot' && isAffBot) {
                dispatch(
                    editStatus({ name: 'queueStatus', value: 'loading' })
                );
                if (props.session.username || (isAffBot && handShakeToken)) {
                    let data = customValues ? JSON.parse(customValues) : {};
                    const customHandShakeToken = data.handshake_token
                        ? JSON.parse(data.handshake_token)
                        : {};
                    const isActive = customHandShakeToken.isActive
                        ? customHandShakeToken.isActive
                        : 'false';
                    sendCheckVipEvent({
                        chatId: props.session.chatId,
                        username: props.session.username || '',
                        usernameFromUrl: true, //check
                        recheckVip: false,
                        merchant: data.hasOwnProperty('merchant') ? data.merchant : '',
                        market: data.hasOwnProperty('market') ? data.market : '',
                        checkVipDetails: data['check_vip_details']
                            ? data['check_vip_details']
                            : null,
                        bbuPromoApiUrl: data.hasOwnProperty('bbu_promo_api_url')
                            ? data.bbu_promo_api_url
                            : '',
                        bbuPromoDefaultUrl: data.hasOwnProperty('bbu_promo_default_url')
                            ? data.bbu_promo_default_url
                            : '',
                        specUsername: props.session.username,
                        handShakeToken: handShakeToken,
                        affiliate_code: affiliateCode || '',
                        isActive: isActive,
                        brandCode: brandCode,
                        redisUrl     : props.session.apiPath,
                        redisSessionId: props.session.botId.toString()+props.session.sessionId,
                        marketCode: marketCode,
                        isVipBot: isVipBot,
                    });
                }
                startSessionChecker(props.session.chatId);
            } else if (props.session.chatMode === 'bot') {
                if (refreshWhenBot && laAffinityToken && laSessionKey) {
                    console.log("refresh when bot triggered");
                    sendChatEndEvent({
                        chatId: chatId,
                        affinityToken: laAffinityToken,
                        key: laSessionKey,
                        memberCode: memberCode,
                        brandCode: brandCode,
                        isVipBot: isVipBot,
                        isAffBot: isAffBot
                    });
                } else if (replyStatus === "LIVEAGENT") {
                    dispatch(editSession({ name: 'chatMode', value: 'agent' }));
                    setCacheByField(props.session.sessionId, 'chatMode', 'agent');
                    dispatch(editStatus({ name: 'queueStatus', value: 'queueLoading' }));
                    setCacheByField(props.session.sessionId, 'queueStatus', 'queueLoading');
                    dispatch(editStatus({ name: 'isAgentTransferPending', value: false }));
                    setCacheByField(props.session.sessionId, 'isAgentTransferPending', false );
                    let data = customValues ? JSON.parse(customValues) : {};
                    let agentAuthEnabled = String(data?.agent_auth_enabled ?? 'true').toLowerCase() === 'true';
                    sendConnectAgentEvent({
                        chatId: chatId,
                        isStartAgent: false,
                        isStartBot: true,
                        isVip: props.session.isVip[0],
                        triggerTime: new Date().getTime().toString(),
                        language: 'chinese',
                        screen_resolution:
                            window.screen.width + 'x' + window.screen.height,
                        userAgent: navigator.userAgent,
                        nps: props.session.nps,
                        matchStartDateTime: matchStartDate,
                        betslipId: betslipId,
                        type: 'connect',
                        username: username,
                        queue: props.session.queue,
                        sfQueueId: '',
                        sfReferrer: referrerSite? referrerSite : document.referrer,
                        connect_flag: "Gloqo Connect",
                        cacheId: props.session.botId.toString()+props.session.sessionId,
                        cacheUrl : props.session.apiPath,
                        browser: getBrowser(),
                        sfPlatform: getPlatform(),
                        acqQueueFlag: data.hasOwnProperty('acq_queue_flag') ? data.acq_queue_flag : "",
                        enQueue: enQueue,
                        acqQueueDays: data.hasOwnProperty('acq_queue_days') ? data.acq_queue_days : "",
                        caseNumber: caseNumber,
                        liveAgentFlag: JSON.parse(customValues).hasOwnProperty('live_agent_mode') ? JSON.parse(customValues).live_agent_mode : '0',
                        isAuthenticated: isAuthenticated,
                        memberCode: memberCode,
                        brandCode: brandCode,
                        marketCode: marketCode,
                        agentAuthEnabled: agentAuthEnabled,
                        isVipBot: isVipBot,
                        botName: botName,
                        isAffBot: isAffBot
                    });
                } else {
                    dispatch(
                        editStatus({
                            name: 'queueStatus',
                            value: "established",
                        })
                    );
                }
                // else if (props.session.isCached && live_agent_mode == '1') {
                //     console.log('sse reconnect', props.session.isCached, conversationId)
                //     if (queueStatus == 'queueing'){
                //         dispatch(
                //             editStatus({
                //                 name: 'queueStatus',
                //                 value: queueStatus,
                //             })
                //         );
                //     }
                //     sendReconnectSseEvent({
                //         chatId: props.session.chatId,
                //         memberCode: memberCode,
                //         brandCode: brandCode
                //     });
                // }
                verifyVipChecked();
                setChatModeLocal('bot');
                console.log('chats after connected', chats);
                if (props.systemMessages.welcome) {
                    if (welcomeMessageSent === false &&
                        props.session.username &&
                        props.session.username != ''
                    ){
                        const now = new Date();
                        const welcome_chat_json: ChatInterface = {
                            owner: 'bot',
                            name: props.settings.botName,
                            type: 'text',
                            firstMessage: true,
                            message: {
                                links: props.systemMessages.welcome.links
                                    ? props.systemMessages.welcome.links.length > 0
                                        ? props.systemMessages.welcome.links
                                        : null
                                    : null,
                                content: props.systemMessages.welcome.message,
                            },
                            displayTime: format(now, 'hh:mmaa'),
                            createdBy: now.getTime(),
                        };

                        dispatch(addChat(welcome_chat_json));

                        dispatch(
                            editSession({
                                name: 'welcomeMessageSent',
                                value: true,
                            })
                        );

                        setCacheByField(
                            props.session.sessionId,
                            'welcomeMessageSent',
                            true
                        );

                        dispatch(
                            editSessions({
                                isStartBot: true,
                                chatCountReason: 'isStartBot',
                            })
                        );

                        setCacheByField(
                            props.session.sessionId,
                            'isStartBot',
                            true
                        );

                        pushCache(
                            props.session.sessionId + '_chats',
                            JSON.stringify(welcome_chat_json)
                        );
                    }

                    if (!welcomeUploadAcked) {
                        const cacheInfo = {
                            sessionId:
                                props.session.botId.toString() +
                                props.session.sessionId,
                            cacheUrl: props.session.apiPath,
                            botName: botName,
                        };

                        sendUploadChatEvent({
                            chatId: props.session.chatId,
                            speaker: 'Bot',
                            speakerName: props.settings.botName,
                            datetime: new Date().toJSON(),
                            message: props.systemMessages.welcome.message,
                            isWelcomeMessage: true,
                            cacheInfo: cacheInfo,
                        });
                    }
                }

                if (props.session.sentCaseDetails == false && caseNumber && caseCategory){
                    setTimeout(() => sendDetails('case', false), 1000);
                } else if (props.session.sentBetSlip == false && checkBetSlipValue()){
                    setTimeout(() => sendDetails('betslip', false), 1000);
                }

                // if (betslipId) {
                //     setChatModeLocal('agent');
                //     setCacheByField(props.session.sessionId, 'chatMode', 'agent');
                //     dispatch(editSession({ name: 'chatMode', value: 'agent' }));
                //     verifyVipChecked();
                //     connectAgent({isVip: vipVals[0], flag: "betSlipID"});
                // } 
            }
            startSessionChecker(props.session.chatId);
            // setChatId(props.session.chatId);
        }
        // console.log(
        //     'update',
        //     dispatch(
        //         sendChatCounts({
        //             data: { isStartBot: true },
        //         })
        //     )
        // );
    };

    const handleMemberVipStatus = async (event: ReceiveEventInterface) => {
        
        if (event.data.recheckVip == false) {
            const activeMemberSessionToken = event.data.activeMemberSessionToken;
            const botIdPrefix = props.session.botId.toString();
            let sessionToken = String(activeMemberSessionToken ?? '').trim();
            if (sessionToken.startsWith(botIdPrefix)) {
                sessionToken = sessionToken.slice(botIdPrefix.length);
            }
            if (
                event.data.isActiveMemberSession === true &&
                sessionToken !== '' &&
                sessionToken !== props.session.sessionId
            ) {
                const cookiesKey =
                    props.session.acctId.toString() +
                    ('00' + props.session.botId).slice(-3) +
                    '_token';
                setCookies(cookiesKey, sessionToken);
                const url = new URL(window.location.href);
                url.searchParams.set('token', sessionToken);
                window.location.href = decodeURIComponent(url.href);
                return;
            }
        }
        if (event.data.usernameFromUrl && isAffBot && !event.data.isAuthenticated){
            dispatch(editSession({ name: 'username', value: '' }));
            setCacheByField(props.session.sessionId, 'username', '');
            dispatch(editSession({ name: 'handShakeToken', value: '' }));
            dispatch(editStatus({ name: 'queueStatus', value: 'established' }));
            return;
        }
        
        // dispatch(editSession({ name: 'isVip', value: event.data.isVip }));
        // setCacheByField(props.session.sessionId, 'isVip', event.data.isVip);
        dispatch(editSession({ name: 'isVip', value: [event.data.isVip, "checked"] }));
        vipVals = [event.data.isVip, "checked"];
        setCacheByField(props.session.sessionId, 'isVip', [event.data.isVip, "checked"]);
        setCacheByField(props.session.sessionId, 'status', 'active');
        setCacheByField(props.session.sessionId, 'chatId', chatId);

        // let queueToAgent = isAssignedToAgent();
        let queueToAgent = false

        if (event.data.recheckVip == false){
            
            dispatch(editSession({ name: 'isAuthenticated', value: event.data.isAuthenticated }));
            setCacheByField(props.session.sessionId, 'isAuthenticated', event.data.isAuthenticated);

            dispatch(editSession({ name: 'memberCode', value: event.data.memberCode }));
            setCacheByField(props.session.sessionId, 'memberCode', event.data.memberCode);

            if (isVipBot && !event.data.isAuthenticated){
                dispatch(editSession({ name: 'chatId', value: "" }));
                setCacheByField(props.session.sessionId, 'chatId', "");
                const memberCodeForEnd =
                    event.data.memberCode ?? store.getState().session.memberCode;
                const baseUrl = (dataCustomValues?.main_site_url || "").replace(/\/$/, "");
                const finalUrl = `${baseUrl}/login?frompage=vipchat`;
                try {
                    sendBotChatEndEvent({
                        chatId: props.session.chatId,
                        isAuthenticated: event.data.isAuthenticated,
                        memberCode: memberCodeForEnd,
                        brandCode: brandCode
                    });
                    endSession(props.session.sessionId);
                } finally {
                    redirectNativeAppOrMainSite(
                        dataCustomValues?.native_app_id,
                        finalUrl
                    );
                }
            } else {
                var mode
                if (event.data.proactiveConnect == true){
                    mode = 'agent'
                    console.log('Proactive Connect: ', event.data.proactiveConnect)
                    dispatch(
                        editStatus({ name: 'disableSurvey', value: true })
                    );
                    setCacheByField(props.session.sessionId, 'disableSurvey', true);
                } else {
                    mode = session.percentage
                    ? generateChatMode(parseInt(session.percentage), session.queue, queueToAgent)
                    : event.data.isVip
                    ? generateChatMode(
                        props.settings.routingPercentageForVIP,
                        session.queue,
                        queueToAgent
                    )
                    : generateChatMode(props.settings.routingPercentage, session.queue, queueToAgent);
                    console.log(
                        'Routing Percentage:',
                        mode,
                        session.percentage
                            ? session.percentage
                            : event.data.isVip
                            ? props.settings.routingPercentageForVIP
                            : props.settings.routingPercentage
                    );
                }
                if (mode === 'bot' || event.data.specMode === 'bot') {
                    if (queueStatus != 'queueingDialog'){
                        dispatch(
                            editStatus({ name: 'queueStatus', value: 'established' })
                        );
                    }
                    dispatch(editSession({ name: 'chatMode', value: 'bot' }));
                    setCacheByField(props.session.sessionId, 'chatMode', 'bot');

                    dispatch(editSession({ name: 'queueStatus', value: 'established' }));
                    setCacheByField(props.session.sessionId, 'queueStatus', 'established');

                    setChatModeLocal('bot');
                    if (
                        welcomeMessageSent === false &&
                        props.session.username &&
                        props.session.username != '' &&
                        props.systemMessages.welcome
                    ) {
                        let newLinks = JSON.parse(JSON.stringify(props.systemMessages.welcome.links));
                        let bbuPromoPattern = "BBU_PROMO_URL";
                        let newUrl = ""
                        if (event.data.bbuPromoUrl){
                            newUrl = event.data.bbuPromoUrl;
                            if (newLinks.length > 0){
                                for (let i = 0; i < newLinks.length; i++){
                                    if (newLinks[i].url == bbuPromoPattern){
                                        newLinks[i].url = newUrl
                                    }
                                }
                            }
                        }
                        const welcome_chat_json: ChatInterface = {
                            owner: 'bot',
                            name: props.settings.botName,
                            type: 'text',
                            firstMessage: true,
                            message: {
                                links: newLinks
                                    ? newLinks.length > 0
                                        ? newLinks
                                        : null
                                    : null,
                                content: props.systemMessages.welcome.message,
                            },
                            displayTime: format(new Date(), 'hh:mmaa'),
                            createdBy: new Date().getTime(),
                        };
                        dispatch(addChat(welcome_chat_json));
                        dispatch(editSession({ name: 'welcomeMessageSent', value: true }));
                        setCacheByField(props.session.sessionId, 'welcomeMessageSent', true );
                        dispatch(
                            editSessions({
                                isStartBot: true,
                                chatCountReason: 'isStartBot',
                            })
                        );
                        setCacheByField(props.session.sessionId, 'isStartBot', true);
                        pushCache(
                            props.session.sessionId + '_chats',
                            JSON.stringify(welcome_chat_json)
                        );
                        let cacheInfo = {
                            sessionId: props.session.botId.toString() + props.session.sessionId,
                            cacheUrl: props.session.apiPath,
                            botName: botName
                        }
                        sendUploadChatEvent({
                            chatId: chatId,
                            speaker: 'Bot',
                            speakerName: props.settings.botName,
                            datetime: new Date().toJSON(),
                            message: props.systemMessages.welcome.message,
                            isWelcomeMessage: true,
                            cacheInfo: cacheInfo
                        });                    
                    }
                    if (props.session.sentCaseDetails == false && caseNumber && caseCategory){
                        setTimeout(() => sendDetails('case', false), 1000);
                    } else if (props.session.sentBetSlip == false && checkBetSlipValue()){
                        setTimeout(() => sendDetails('betslip', false), 1000);
                    } 
                } else {
                    setChatModeLocal('agent');
                    setCacheByField(props.session.sessionId, 'chatMode', 'agent');
                    dispatch(editSession({ name: 'chatMode', value: 'agent' }));
                    connectAgent({isVip: event.data.isVip, flag: "handleVipStatus"});
                    checkIfStartBot();
                    if (!isStartBot) {
                        dispatch(
                            editSessions({
                                isStartAgent: true,
                                chatCountReason: 'isStartAgent',
                            })
                        );
                        setCacheByField(props.session.sessionId, 'isStartAgent', true);
                    }
                }

                setCacheByField(props.session.sessionId, 'chatRatingDone', false);

                sendUploadChatEvent({
                    chatId: chatId,
                    speaker: 'Client',
                    speakerName: props.session.username,
                    datetime: new Date().toJSON(),
                    message: "(System message for sending username)",
                    is_system: true,
                })
            }
            // startSessionChecker();
        }
    };

    // const handleAuthenticatedEvent = (event: ReceiveAuthenticatedEvent) => {
    //     dispatch(
    //         editSession({
    //             name: 'isChecked',
    //             value: event.data.isChecked,
    //         })
    //     );
    // };
    const handleStoredChatHistoriesList = async (event: ReceiveEventInterface) => {
        console.log("handleStoredChatHistoriesList", event.data)
        dispatch(
            editSession({
                name: 'chatHistoriesListStatus',
                value: true,
            })
        );
        setCacheByField(props.session.sessionId, 'chatHistoriesListStatus', true);

        dispatch(editSession({ name: 'conversationEntryId', value: event.data.conversationEntryId }));
        setCacheByField(props.session.sessionId, 'conversationEntryId', event.data.conversationEntryId);

        dispatch(editSession({ name: 'accessToken', value: event.data.accessToken }));
        setCacheByField(props.session.sessionId, 'accessToken', event.data.accessToken);
        
        dispatch(editSession({ name: 'conversationId', value: event.data.conversationId }));
        setCacheByField(props.session.sessionId, 'conversationId', event.data.conversationId);
        
        // var alertMessage = t("alertReadyLoadChatHistoriesList")
        // dispatch(editStatus({name: "alertMessage", value: alertMessage}))
        // dispatch(
        //     editStatus({
        //         name: 'isAlertModal',
        //         value: true,
        //     })
        // )
    }

    const handleLoginStatus = async (event: ReceiveEventInterface) => {
        // Close Loading Modal
        dispatch(
            editStatus({
                name: 'isLoadingModal',
                value: false,
            })
        );
        if (event.data.login_status) {
            let cacheInfo = {
                sessionId: props.session.botId.toString() + props.session.sessionId,
                cacheUrl: props.session.apiPath,
                botName: botName
            }
            if (processor == 2 || processor == 3){
                let data = {
                    message: "Done (login successful)",
                    type: 'message',
                    username: username,
                    chatId: chatId,
                    customer_lang_culture:
                        botLanguage.toLowerCase() === 'chinese'
                            ? 'zh-cn'
                            : botLanguage.toLowerCase() === 'thai'
                            ? 'th-th'
                            : botLanguage.toLowerCase() === 'vietnamese'
                            ? 'vi-vn'
                            : botLanguage.toLowerCase() === 'vietnam'
                            ? 'vi-vn'
                            : botLanguage.toLowerCase() === 'english'
                            ? 'en'
                            : botLanguage.toLowerCase(),
                    operating_system: getOS(navigator),
                    screen_resolution:
                        window.screen.width + '*' + window.screen.height,

                    platform: 'mario',
                    group_name: '',
                    processor: processor,
                    cacheInfo: cacheInfo,
                    brandInfo : {
                        brand: brandCode,
                        market: marketCode
                    },
                };
                sendMessageEvent(data);
                
                dispatch(editSession({ name: 'username', value: event.data.username }));
                setCacheByField(props.session.sessionId, 'username', event.data.username);
                
                dispatch(editSession({ name: 'waitingAnswerReply', value: true }));
                setCacheByField(props.session.sessionId, 'waitingAnswerReply', true)
                
                dispatch(editSession({ name: 'isAuthenticated', value: true }));
                setCacheByField(props.session.sessionId, 'isAuthenticated', true);

                dispatch(editSession({ name: 'isVip', value: [event.data.isVip, "checked"] }));
                setCacheByField(props.session.sessionId, 'isVip', [event.data.isVip, "checked"]);

                dispatch(editSession({ name: 'memberCode', value: event.data.memberCode }));
                setCacheByField(props.session.sessionId, 'memberCode', event.data.memberCode);                
                
                // Open Success Login Model
                dispatch(
                    editStatus({
                        name: 'isSuccessLoginModal',
                        value: true,
                    })
                );
                console.log('SUCCESS LOGIN');
            } else if (processor == 0){
                let cacheInfo = {
                    sessionId: props.session.botId.toString() + props.session.sessionId,
                    cacheUrl: props.session.apiPath,
                    botName: botName
                }
                let data = {
                    username: username,
                    chatId: chatId,
                    type: 'goToIntent',
                    intentId: intentId,
                    triggerTime: new Date().toJSON(),
                    platform: 'mario',
                    group_name: '',
                    customer_lang_culture:
                        botLanguage.toLowerCase() ===
                        'chinese'
                            ? 'zh-cn'
                            : botLanguage.toLowerCase() ===
                            'thai'
                            ? 'th-th'
                            : botLanguage.toLowerCase() ===
                            'vietnamese'
                            ? 'vi-vn'
                            : botLanguage.toLowerCase() ===
                            'vietnam'
                            ? 'vi-vn'
                            : botLanguage.toLowerCase() ===
                            'english'
                            ? 'en'
                            : botLanguage.toLowerCase(),
                    operating_system: getOS(navigator),
                    screen_resolution:
                        window.screen.width +
                        '*' +
                        window.screen.height,
                    processor: processor,
                    cacheInfo: cacheInfo,
                    brandInfo : {
                        brand: brandCode,
                        market: marketCode
                    }
                } as SendIntentEventInterface;
                sendIntentEvent(data);
            };    
        } else {
            if (processor == 2 || processor == 3){
                if (event.data.description == "[ERROR_ON_LOGIN]"){
                    event.data.description = t('error_login')
                }
                let data = {
                    message: "Done (login not successful)",
                    type: 'message',
                    username: username,
                    chatId: chatId,
                    customer_lang_culture:
                        botLanguage.toLowerCase() === 'chinese'
                            ? 'zh-cn'
                            : botLanguage.toLowerCase() === 'thai'
                            ? 'th-th'
                            : botLanguage.toLowerCase() === 'vietnamese'
                            ? 'vi-vn'
                            : botLanguage.toLowerCase() === 'vietnam'
                            ? 'vi-vn'
                            : botLanguage.toLowerCase() === 'english'
                            ? 'en'
                            : botLanguage.toLowerCase(),
                    operating_system: getOS(navigator),
                    screen_resolution:
                        window.screen.width + '*' + window.screen.height,
        
                    platform: 'mario',
                    group_name: '',
                    processor: processor,
                    brandInfo : {
                        brand: brandCode,
                        market: marketCode
                    },
                };
                sendMessageEvent(data);
            }
            // OPEN LOGIN FAIL MODAL
            dispatch(
                editStatus({
                    name: 'isFailLoginModal',
                    value: true,
                })
            );
            console.log('LOGIN FAILED');
        }
    };

    const handleTerminateStatus = (event: ReceiveEventInterface) => {
        const terminateStatus = event.data.isTerminated;
        if (terminateStatus) {
            // connectAgent();
            console.log("terminate status", terminateStatus)
            endSession(props.session.sessionId);
            window.location.reload();
        } else {
            sendVerifyReconnectionEvent({
                chatId,
                affinityToken: props.session.laAffinityToken,
                key: props.session.laSessionKey,
            });
            // dispatch(
            //     editSessions({
            //         laAffinityToken: event.data.live_agent_info.affinityToken,
            //         laSessionKey: event.data.live_agent_info.key,
            //         laSessionId: event.data.live_agent_info.id,
            //     })
            // );
        }
    };

    const handleReconnectionVerified = (event: ReceiveEventInterface) => {
        if (event.data.isVerified === 'OK') {
            console.log("handleReconnectionVerified", chatId)

            if (queueStatus == 'queueingDialog'){
                dispatch(
                    editStatus({
                        name: 'queueStatus',
                        value: queueStatus,
                    })
                );
            }

            dispatch(
                editStatus({
                    name: 'queueStatus',
                    value: 'established',
                })
            );

            if (chatId) {
                sendConsumeMessageEvent({
                    chatId,
                });

                sendPingEvent(chatId);
            }
        } else {
            setCacheByField(props.session.sessionId, 'chatMode', 'agent');
            dispatch(editSession({ name: 'chatMode', value: 'agent' }));
            verifyVipChecked();

            connectAgent({ isVip:vipVals[0], flag: 'handleReconnectVerified'});
        }

        // if (props.session.sentCaseDetails == false && caseNumber && caseCategory){
        //     // dispatch(
        //     //     addDialog({
        //     //         task: 'caseDetails',
        //     //         type: 'action',
        //     //         data: '',
        //     //     })
        //     // );
        //     sendCaseDetails(props.session.laAffinityToken, props.session.key)
        // }

        // if(checkBetSlipValue() || props.session.betSlipVals){

        //     let templist = props.session.betSlipVals.split(',');

        //     dispatch(
        //         editSession({
        //             name: 'betSlipVals',
        //             value: templist,
        //         })
        //     );

        //     if (checkBetSlipValue() && props.session.sentBetSlip.toString() !== betslipId){
    
        //         let slipVals = [vendor, status, betslipId, matchStartDate];
        //         dispatch(
        //             editSession({
        //                 name: 'betSlipVals',
        //                 value: slipVals,
        //             })
        //         );

        //         dispatch(
        //             addDialog({
        //                 task: 'betSlip',
        //                 type: 'action',
        //                 data: '',
        //             })
        //         );
    
        //         setCacheByField(props.session.sessionId, 'betSlipVals', slipVals);

        //     } else if (props.session.sentBetSlip.toString() !== templist[2]) {
        //         dispatch(
        //             addDialog({
        //                 task: 'betSlip',
        //                 type: 'action',
        //                 data: '',
        //             })
        //         );
        //     }

        // }
    };

    const checkIfStartBot = () => {
        const getValue = getCacheByField(props.session.sessionId, 'isStartBot')
        getValue.then((value:any) => {
            const isStartBot = value
            if (isStartBot === 'true') {
                dispatch(
                    editSession({
                        name: 'isStartBot',
                        value: true,
                    })
                );
                dispatch(
                    editSession({
                        name: 'respondedToBot',
                        value: true,
                    })
                );
                dispatch(
                    editSession({
                        name: 'isStartAgent',
                        value: false,
                    })
                );
                dispatch(
                    editSessions({
                        isBotToAgent: true,
                        chatCountReason: 'isBotToAgent',
                    })
                );
                setCacheByField(props.session.sessionId, 'respondedToBot', true)
                setCacheByField(props.session.sessionId, 'isStartAgent', false)
                setCacheByField(props.session.sessionId, 'isBotToAgent', true)
            }
        })
    }

    const handleReplyEvent = async (event: ReceiveEventInterface) => {
        info('handleReplyEvent', 'Reply:', event);
        await dispatch(editStatus({ name: 'isBotTyping', value: false }));
        dispatch(editSession({ name: 'waitingAnswerReply', value: false }));
        setCacheByField(props.session.sessionId, 'waitingAnswerReply', false);
        if ((processor == 2 || processor == 3) && chatModeLocal === 'bot'){
            const actionItem = event.data.find((item: any) => item.type === 'action');
            let action = actionItem ? actionItem.content.action : "MESSAGE";
            event.data.map((item: any, index: number, arr: any[]) => {
                if (item.type === 'text') {
                    const content = {
                        owner: 'bot',
                        type: 'text',
                        name: props.settings.botName,
                        message: {
                            links: item.content.links ? item.content.links : null,
                            content: item.content.message,
                            ...(item.content.transactionId ? { transactionId: item.content.transactionId } : {}),
                        },
                        displayTime: format(new Date(), 'hh:mmaa'),
                        createdBy: new Date().getTime(),
                        messageId: item.messageId
                    } as ChatInterface;
                    dispatch(addChat(content));
                    // pushCache(
                    //     props.session.sessionId + '_chats',
                    //     JSON.stringify(content)
                    // );
                } else if (item.type === 'summary') {
                    const content = {
                        owner: 'bot',
                        type: 'summary',
                        name: props.settings.botName,
                        message: {
                            links: item.content.links ? item.content.links : null,
                            content: item.content.summary,
                        },
                        displayTime: format(new Date(), 'hh:mmaa'),
                        createdBy: new Date().getTime(),
                    } as ChatInterface;
                //     pushCache(
                //         props.session.sessionId + '_chats',
                //         JSON.stringify(content)
                //     );
                // } else if (item.type === 'action') {
                //     action = item.content.action
                }
            });

            handleReplyAction(action);

        } else { 
            event.data.map((item: any, index: number, arr: []) => {
                if (item.type === 'text') {
                    const content = {
                        owner: 'bot',
                        type: 'text',
                        name: props.settings.botName,
                        message: {
                            links: item.content.links ? item.content.links : null,
                            content: item.content.message,
                        },
                        displayTime: format(new Date(), 'hh:mmaa'),
                        createdBy: new Date().getTime(),
                        messageId: item.messageId
                    } as ChatInterface;
                    if (item.content.links) {
                        let intents = item.content.links.filter(
                            (link: any) => link.type == 'goToIntent'
                        );
                        if (intents.length > 0) {
                            dispatch(
                                editStatus({
                                    name: 'intentId',
                                    value: intents[intents.length - 1].intentId,
                                })
                            );
                        }
                    }
                    dispatch(addChat(content));
                    // pushCache(
                    //     props.session.sessionId + '_chats',
                    //     JSON.stringify(content)
                    // );
                } else if (item.type === 'quickreply') {
                    dispatch(
                        addDialog({
                            task: 'connectAgent',
                            type: 'action',
                        })
                    );
                } else if (item.type === 'image') {
                    const content = {
                        owner: 'bot',
                        type: 'image',
                        name: props.settings.botName,
                        message: {
                            links: item.content.links ? item.content.links : null,
                            url: item.content.url,
                            description: item.content.description,
                            content: item.content.message
                                ? item.content.message
                                : null,
                        },
                        displayTime: format(new Date(), 'hh:mmaa'),
                        createdBy: new Date().getTime(),
                    } as ChatInterface;
                    dispatch(addChat(content));
                    // pushCache(
                    //     props.session.sessionId + '_chats',
                    //     JSON.stringify(content)
                    // );
                } else if (item.type == 'video') {
                    dispatch(
                        addChat({
                            owner: 'bot',
                            type: 'video',
                            name: props.settings.botName,
                            message: {
                                links: item.content.links
                                    ? item.content.links
                                    : null,
                                url: item.content.url,
                                description: item.content.description,
                                content_type: 'video',
                            },
                            displayTime: format(new Date(), 'hh:mmaa'),
                            createdBy: new Date().getTime(),
                        } as ChatInterface)
                    );
                } else if (item.type === 'form') {
                    dispatch(
                        editSession({
                            name: 'formContent',
                            value: item.content,
                        })
                    );
                    dispatch(
                        editStatus({
                            name: 'isFormModal',
                            value: true,
                        })
                    );
                } else if (item.type === 'imageLink'){

                    let imageMessage = "";
                    const uploadFailed = item.content.message === "Error uploading image";
                    imageMessage = uploadFailed ? "fileUploadFailure" : "fileUploadSuccess";

                    // MIAW uploads are already sent as chasitorChatMessage.
                    if (live_agent_flag !== '1') {
                        sendChasitorMessageEvent({
                            chatId: props.session.chatId,
                            affinityToken: props.session.laAffinityToken,
                            key: props.session.laSessionKey,
                            message: uploadFailed
                                ? t("fileUploadFailure")
                                : t("fileUploadAgentSuccess") + item.content.message,
                            type: 'message',
                            username: props.session.username,
                            isVipBot: isVipBot,
                            isAffBot: isAffBot
                        });
                    }

                    const content = {
                        owner: 'user',
                        name: username,
                        type: 'text',
                        message: {
                            content: t(imageMessage),
                        },
                        displayTime: format(new Date(), 'hh:mmaa'),
                        createdBy: new Date().getTime(),
                    } as ChatInterface;
                    pushCache(props.session.sessionId + '_chats', JSON.stringify(content));
                    dispatch(addChat(content));
                }
                dispatch(incStatus({ name: 'notificationSounds' }));
            });
        }
    };

    const handleReplyAction = async (action: string) => {
        const isAuthenticatedStr = await getCacheByField(props.session.sessionId, 'isAuthenticated');
        const isAuthenticated = isAuthenticatedStr === "true";
        const state = store.getState();
        const memberCode = state.session.memberCode;
        action = action.toUpperCase()
        switch (action) {
            case "LIVEAGENT": // Connect Agent Action
                setTimeout(() => {
                    setCacheByField(props.session.sessionId, 'chatMode', 'agent');
                    dispatch(editSession({ name: 'chatMode', value: 'agent' }));
                    dispatch(editStatus({ name: 'queueStatus', value: 'queueLoading' }));
                    setCacheByField(props.session.sessionId, 'queueStatus', 'queueLoading');
                    let data = customValues ? JSON.parse(customValues) : {};
                    let agentAuthEnabled = String(data?.agent_auth_enabled ?? 'true').toLowerCase() === 'true';
                    sendConnectAgentEvent({
                        chatId: chatId,
                        isStartAgent: false,
                        isStartBot: true,
                        isVip: props.session.isVip[0],
                        triggerTime: new Date().getTime().toString(),
                        language: 'chinese',
                        screen_resolution:
                            window.screen.width + 'x' + window.screen.height,
                        userAgent: navigator.userAgent,
                        nps: props.session.nps,
                        matchStartDateTime: matchStartDate,
                        betslipId: betslipId,
                        type: 'connect',
                        username: username,
                        queue: props.session.queue,
                        sfQueueId: '',
                        sfReferrer: referrerSite? referrerSite : document.referrer,
                        connect_flag: "Gloqo Connect",
                        cacheId: props.session.botId.toString()+props.session.sessionId,
                        cacheUrl : props.session.apiPath,
                        browser: getBrowser(),
                        sfPlatform: getPlatform(),
                        acqQueueFlag: data.hasOwnProperty('acq_queue_flag') ? data.acq_queue_flag : "",
                        enQueue: enQueue,
                        acqQueueDays: data.hasOwnProperty('acq_queue_days') ? data.acq_queue_days : "",
                        caseNumber: caseNumber,
                        liveAgentFlag: JSON.parse(customValues).hasOwnProperty('live_agent_mode') ? JSON.parse(customValues).live_agent_mode : '0',
                        isAuthenticated: isAuthenticated,
                        memberCode: memberCode,
                        brandCode: brandCode,
                        marketCode: marketCode,
                        agentAuthEnabled: agentAuthEnabled,
                        isVipBot: isVipBot,
                        botName: botName,
                        isAffBot: isAffBot
                    });
                }, 1000);
                break;
    
            case "ENDCHAT": // Chat End Action
                dispatch(addDialog({ task: 'leaved', type: 'action' }));
                sendBotChatEndEvent({
                    chatId:props.session.chatId,
                    isAuthenticated: isAuthenticated,
                    memberCode: memberCode,
                    brandCode: brandCode
                })
                endSession(props.session.sessionId);
                editSession({ name: 'disableChatRatingSubmitted', value: true })
                dispatch(
                    editStatus({
                        name: 'isChatRatingModal',
                        value: true,
                    })
                );
                break;
    
            case "ERROR": // Error Response Action
                setTimeout(() => {
                    setCacheByField(props.session.sessionId, 'chatMode', 'agent');
                    dispatch(editSession({ name: 'chatMode', value: 'agent' }));
                    dispatch(editStatus({ name: 'queueStatus', value: 'queueLoading' }));
                    setCacheByField(props.session.sessionId, 'queueStatus', 'queueLoading');
                    let data = customValues ? JSON.parse(customValues) : {};
                    let agentAuthEnabled = String(data?.agent_auth_enabled ?? 'true').toLowerCase() === 'true';
                    sendConnectAgentEvent({
                        chatId: chatId,
                        isStartAgent: false,
                        isStartBot: true,
                        isVip: props.session.isVip[0],
                        triggerTime: new Date().getTime().toString(),
                        language: 'chinese',
                        screen_resolution:
                            window.screen.width + 'x' + window.screen.height,
                        userAgent: navigator.userAgent,
                        nps: props.session.nps,
                        matchStartDateTime: matchStartDate,
                        betslipId: betslipId,
                        type: 'connect',
                        username: username,
                        queue: props.session.queue,
                        sfQueueId: '',
                        sfReferrer: referrerSite? referrerSite : document.referrer,
                        connect_flag: "Gloqo Connect",
                        cacheId: props.session.botId.toString()+props.session.sessionId,
                        cacheUrl : props.session.apiPath,
                        browser: getBrowser(),
                        sfPlatform: getPlatform(),
                        acqQueueFlag: data.hasOwnProperty('acq_queue_flag') ? data.acq_queue_flag : "",
                        enQueue: enQueue,
                        acqQueueDays: data.hasOwnProperty('acq_queue_days') ? data.acq_queue_days : "",
                        caseNumber: caseNumber,
                        liveAgentFlag: JSON.parse(customValues).hasOwnProperty('live_agent_mode') ? JSON.parse(customValues).live_agent_mode : '0',
                        isAuthenticated: isAuthenticated,
                        memberCode: memberCode,
                        brandCode: brandCode,
                        marketCode: marketCode,
                        agentAuthEnabled: agentAuthEnabled,
                        isVipBot: isVipBot,
                        botName: botName,
                        isAffBot: isAffBot
                    });
                }, 1000);
                break;
            default:
                break;
        }
    }

    const handleMessageInsertedEvent = async (event: ReceiveEventInterface) => {
        console.log('handleMessageInsertedEvent', chatId, props.session.chatId);
        if (chatId) {
            sendConsumeMessageEvent({
                chatId,
            });
        }
    };

    const handleUploadFileStatusEvent = async (event: ReceiveEventInterface) => {
        // Close loading modal
        dispatch(
            editStatus({
                name: 'isLoadingModal',
                value: false,
            })
        );

        dispatch(editStatus({name: "uploadReceiptType", value: event.data.receiptType}))
        dispatch(editStatus({name: "uploadStatus", value: event.data.isSuccess}))
        dispatch(
            editStatus({
                name: 'isActionStatusModal',
                value: true,
            })
        )

        if (event.data.isSuccess == true){
            // Success display upload image
            const image_content = {
                owner: 'user',
                name: username,
                type: 'image',
                message: {
                    url: event.data.imageLink,
                },
                displayTime: format(new Date(), 'hh:mmaa'),
                createdBy: new Date().getTime(),
            } as ChatInterface;
            dispatch(addChat(image_content));
        } 

        if (event.data.message){            
            // Display Message
            const content = {
                owner: 'user',
                name: username,
                type: 'text',
                message: {
                    content: event.data.message,
                },
                displayTime: format(new Date(), 'hh:mmaa'),
                createdBy: new Date().getTime(),
            } as ChatInterface;
            dispatch(addChat(content));
        }

        if (isVipBot){
            dispatch(editStatus({ name: 'isBotTyping', value: false }));
            dispatch(editSession({ name: 'waitingAnswerReply', value: false }));
            const state = store.getState();
            const currentChatMode = state.session.chatMode;
            if (currentChatMode === "bot"){
                const memberCode = state.session.memberCode;
                dispatch(editSession({ name: 'chatMode', value: 'agent' }));
                setCacheByField(props.session.sessionId, 'chatMode', 'agent');
                setChatModeLocal('agent');
                dispatch(editStatus({ name: 'queueStatus', value: 'queueLoading' }));
                setCacheByField(props.session.sessionId, 'queueStatus', 'queueLoading');
                let data = customValues ? JSON.parse(customValues) : {};
                let agentAuthEnabled = String(data?.agent_auth_enabled ?? 'true').toLowerCase() === 'true';
                sendConnectAgentEvent({
                    chatId: chatId,
                    isStartAgent: false,
                    isStartBot: true,
                    isVip: props.session.isVip[0],
                    triggerTime: new Date().getTime().toString(),
                    language: 'chinese',
                    screen_resolution:
                        window.screen.width + 'x' + window.screen.height,
                    userAgent: navigator.userAgent,
                    nps: props.session.nps,
                    matchStartDateTime: matchStartDate,
                    betslipId: betslipId,
                    type: 'connect',
                    username: username,
                    queue: props.session.queue,
                    sfQueueId: '',
                    sfReferrer: referrerSite? referrerSite : document.referrer,
                    connect_flag: "VIP Chat",
                    cacheId: props.session.botId.toString()+props.session.sessionId,
                    cacheUrl : props.session.apiPath,
                    browser: getBrowser(),
                    sfPlatform: getPlatform(),
                    acqQueueFlag: data.hasOwnProperty('acq_queue_flag') ? data.acq_queue_flag : "",
                    enQueue: enQueue,
                    acqQueueDays: data.hasOwnProperty('acq_queue_days') ? data.acq_queue_days : "",
                    caseNumber: caseNumber,
                    liveAgentFlag: JSON.parse(customValues).hasOwnProperty('live_agent_mode') ? JSON.parse(customValues).live_agent_mode : '0',
                    isAuthenticated: isAuthenticated,
                    memberCode: memberCode,
                    brandCode: brandCode,
                    marketCode: marketCode,
                    agentAuthEnabled: agentAuthEnabled,
                    isVipBot: isVipBot,
                    botName: botName,
                    isAffBot: isAffBot
                });
            }
        }

    };

    const handleLiveAgentRequestSuccessEvent = (
        event: ReceiveEventInterface
    ) => {

        console.log("new queue check, offline count", offlineCount, "online count", onlineCount);
        const state = store.getState();
        const memberCode = state.session.memberCode;

        if (event.data.newQueue && offlineCount < 1 && onlineCount < 2){

            console.log("new queue value", event.data.newQueue);

            onlineCount = onlineCount + 1;

            let data = customValues ? JSON.parse(customValues) : {};
            let agentAuthEnabled = String(data?.agent_auth_enabled ?? 'true').toLowerCase() === 'true';
            sendConnectAgentEvent({
                chatId: chatId,
                isStartAgent: false,
                isStartBot: true,
                isVip: props.session.isVip[0],
                triggerTime: new Date().getTime().toString(),
                language: 'chinese',
                screen_resolution:
                    window.screen.width + 'x' + window.screen.height,
                userAgent: navigator.userAgent,
                nps: props.session.nps,
                matchStartDateTime: matchStartDate,
                betslipId: betslipId,
                type: 'connect',
                username: username,
                queue: props.session.queue,
                sfQueueId: event.data.newQueue,
                sfReferrer: referrerSite? referrerSite : document.referrer,
                connect_flag: "F1M5 Queue",
                cacheId: props.session.botId.toString()+props.session.sessionId,
                cacheUrl : props.session.apiPath,
                browser: getBrowser(),
                sfPlatform: getPlatform(),
                acqQueueFlag: data.hasOwnProperty('acq_queue_flag') ? data.acq_queue_flag : "",
                enQueue: enQueue,
                acqQueueDays: data.hasOwnProperty('acq_queue_days') ? data.acq_queue_days : "",
                caseNumber: caseNumber,
                memberCode: memberCode,
                brandCode: brandCode,
                marketCode: marketCode,
                agentAuthEnabled: agentAuthEnabled,
                isVipBot: isVipBot,
                botName: botName,
                isAffBot: isAffBot
            });

            sendChatEndEvent({
                chatId: chatId,
                affinityToken: event.data.live_agent_session.affinityToken,
                key: event.data.live_agent_session.key,
                memberCode: memberCode,
                brandCode: brandCode,
                isVipBot: isVipBot,
                isAffBot: isAffBot
            });

        } else {

            let sfPostChatUrl = event.data.message.postChatUrl + "?id=" + event.data.message.visitorId;

            console.log("SF POST CHAT URL", sfPostChatUrl);

            dispatch(
                editStatus({
                    name: 'queueNumber',
                    value: event.data.message.queuePosition - 1,
                })
            );
            dispatch(
                editStatus({
                    name: 'queueStatus',
                    value: 'queueingDialog',
                })
            );
            dispatch(
                editSessions({
                    laAffinityToken: event.data.live_agent_session.affinityToken,
                    laSessionKey: event.data.live_agent_session.key,
                    laSessionId: event.data.live_agent_session.id,
                    postChatUrl: sfPostChatUrl
                })
            );
            dispatch(
                editSession({
                    name: 'refreshWhenQueueing',
                    value: true,
                })
            );
    
            setCacheByField(props.session.sessionId, 'refreshWhenQueueing', true);
            setCacheByField(props.session.sessionId, 'postChatUrl', sfPostChatUrl);
            setCacheByField(props.session.sessionId, 'queueStatus', 'queueingDialog');
            setCacheByField(props.session.sessionId, 'laAffinityToken', event.data.live_agent_session.affinityToken);
            setCacheByField(props.session.sessionId, 'laSessionKey', event.data.live_agent_session.key);
        }

        
    };

    const runAgentsUnavailableOfflineFallback = (logChatId: string) => {
        dispatch(editStatus({ name: 'queueStatus', value: 'failed' }));
        const sfAvailability = store.getState().settings.sfAvailability;
        if (shouldShowWebToCaseMaintenance(sfAvailability)) {
            endSession(props.session.sessionId);
            sendUploadChatEvent({
                chatId: logChatId,
                speaker: 'Client',
                speakerName: username,
                datetime: new Date().toJSON(),
                message:
                    'Web-to-Case skipped: SF/web-to-case unavailable (sf_availability.available=false); redirected to maintenance',
                is_system: true,
            });
            navigate('/maintenance', { replace: true });
            return;
        }

        dispatch(
            addDialog({
                task: 'ended',
                type: 'action',
            })
        );
        dispatch(
            addDialog({
                task: 'offlineForm',
                type: 'action',
            })
        );
        endSession(props.session.sessionId);
        if (webToCaseForm == "chat_rating"){
            dispatch(editStatus({ name: 'isWebToCase', value: true }));
            dispatch(
                editStatus({
                    name: 'isChatRatingModal',
                    value: true,
                })
            );
        } else{
            dispatch(
                editStatus({
                    name: 'isIframeModal',
                    value:
                        true &&
                        props.settings.enableOfflineForm &&
                        props.settings.useExternalLinkForOfflineForm,
                })
            );
            dispatch(
                editStatus({
                    name: 'iframeUrl',
                    value: props.settings.externalLinkForOfflineForm,
                })
            );
        }

        console.log("Live Agent request failed, webtocase triggered");

        sendUploadChatEvent({
            chatId: logChatId,
            speaker: 'Client',
            speakerName: username,
            datetime: new Date().toJSON(),
            message: "Web-to-Case triggered due to Agent Request Failed",
            is_system: true,
        });
    };

    const handleLiveAgentRequestFailEvent = (event: ReceiveEventInterface) => {

        if (offlineCount < 1 && event.data.newQueue){
            const state = store.getState();
            const memberCode = state.session.memberCode;

            console.log("offline new queue value", event.data.newQueue, offlineCount);
            
            let data = customValues ? JSON.parse(customValues) : {};
            let agentAuthEnabled = String(data?.agent_auth_enabled ?? 'true').toLowerCase() === 'true';
            sendConnectAgentEvent({
                chatId: chatId,
                isStartAgent: false,
                isStartBot: true,
                isVip: props.session.isVip[0],
                triggerTime: new Date().getTime().toString(),
                language: 'chinese',
                screen_resolution:
                    window.screen.width + 'x' + window.screen.height,
                userAgent: navigator.userAgent,
                nps: props.session.nps,
                matchStartDateTime: matchStartDate,
                betslipId: betslipId,
                type: 'connect',
                username: username,
                queue: props.session.queue,
                sfQueueId: event.data.newQueue,
                sfReferrer: referrerSite? referrerSite : document.referrer,
                connect_flag: "F1M5 QUEUE",
                cacheId: props.session.botId.toString()+props.session.sessionId,
                cacheUrl : props.session.apiPath,
                browser: getBrowser(),
                sfPlatform: getPlatform(),
                acqQueueFlag: data.hasOwnProperty('acq_queue_flag') ? data.acq_queue_flag : "",
                enQueue: enQueue,
                acqQueueDays: data.hasOwnProperty('acq_queue_days') ? data.acq_queue_days : "",
                caseNumber: caseNumber,
                memberCode: memberCode,
                brandCode: brandCode,
                marketCode: marketCode,
                agentAuthEnabled: agentAuthEnabled,
                isVipBot: isVipBot,
                botName: botName,
                isAffBot: isAffBot
            });

            offlineCount +=1


        } else {
            runAgentsUnavailableOfflineFallback(chatId);
        }

    };

    const handleLiveAgentQueueUpdateEvent = (event: ReceiveEventInterface) => {
        dispatch(
            editStatus({
                name: 'queueNumber',
                value: event.data.message.position,
            })
        );
    };

    const handleCreateConversationEvent = async (event: ReceiveEventInterface) => {
        dispatch(editSession({ name: 'accessToken', value: event.data.accessToken }));
        dispatch(editSession({ name: 'conversationId', value: event.data.conversationId }));
        

        setCacheByField(props.session.sessionId, 'accessToken', event.data.accessToken);
        setCacheByField(props.session.sessionId, 'conversationId', event.data.conversationId);
    }

    const handleAgentJoinedConversation = async (event: ReceiveEventInterface) => {
        if (!isChatAlive) {
            return;
        }
        console.log('sendChatCountsEvent ', chatModeLocal);

        if (event.data.status == "ROUTED"){
            dispatch(editSession({ name: 'accessToken', value: event.data.accessToken }));
            dispatch(editSession({ name: 'conversationId', value: event.data.conversationId }));
        

            setCacheByField(props.session.sessionId, 'accessToken', event.data.accessToken);
            setCacheByField(props.session.sessionId, 'conversationId', event.data.conversationId);
            
            let data = JSON.parse(customValues);
            let sfPostUrl = data['sf_post_url'] ?? "";
            let recordId = event.data.recordId;
        
            if (recordId) {
                let sfPostChatUrl = `${sfPostUrl}?id=${recordId}`;
            
                console.log("SF POST CHAT URL", sfPostChatUrl);
            
                dispatch(
                    editSession({
                        name: 'postChatUrl',
                        value: sfPostChatUrl,
                    })
                );
            
                setCacheByField(props.session.sessionId, 'postChatUrl', sfPostChatUrl);
            }
        
            // const liveAgentConnectedMessage = {
            //     owner: 'system',
            //     name: 'Mario',
            //     type: 'text',
            //     message: {
            //         content: props.systemMessages.live_agent_connected.message,
            //     },
            //     displayTime: format(new Date(), 'hh:mmaa'),
            //     createdBy: new Date().getTime(),
            // } as ChatInterface;
            // dispatch(addChat(liveAgentConnectedMessage));
            // pushCache(
            //     props.session.sessionId + '_chats',
            //     JSON.stringify(liveAgentConnectedMessage)
            // );   
        }

        if (showLiveAgentConnectedMessage){
            const liveAgentConnectedMessage = {
                owner: 'system',
                name: 'Mario',
                type: 'text',
                message: {
                    content: props.systemMessages.live_agent_connected.message,
                },
                displayTime: format(new Date(), 'hh:mmaa'),
                createdBy: new Date().getTime(),
            } as ChatInterface;
            dispatch(addChat(liveAgentConnectedMessage));
            pushCache(
                props.session.sessionId + '_chats',
                JSON.stringify(liveAgentConnectedMessage)
            );
            dispatch(editStatus({ name: 'showLiveAgentConnectedMessage', value: false }));
            setCacheByField(props.session.sessionId, 'showLiveAgentConnectedMessage', false);
        }
        
        const liveAgentJoinedMessage = {
            owner: 'system',
            name: 'Mario',
            type: 'text',
            message: {
                content: props.systemMessages.live_agent_joined.message.replace(
                    /(\{).+?(\})/g,
                    event.data.agentName ?? botName
                ),
            },
            displayTime: format(new Date(), 'hh:mmaa'),
            createdBy: new Date().getTime(),
        } as ChatInterface;
        dispatch(addChat(liveAgentJoinedMessage));

        if (historyTranscriptSent != true && isStartAgent != true) {
            if (!isStartBot) {
                dispatch(
                    editSession({
                        name: 'isStartBot',
                        value: true,
                    })
                );
            };
            dispatch(
                editSessions({
                    isBotToAgent: true,
                    chatCountReason: 'isBotToAgent',
                })
            );
            setCacheByField(props.session.sessionId, 'isBotToAgent', true);
            setCacheByField(props.session.sessionId, 'isStartBot', true);
            // const cache_chats = await popCache(
            //     props.session.sessionId + '_chats'
            // );
            // const cache_summary = await getCacheByField(props.session.sessionId + "_summary", 'summary');

            // console.log('Pop cache', cache_chats);

            // let transcript = `${t('history')}:\n`;
            // cache_chats.map((chat: string, i: number) => {
            //     let chatObj = JSON.parse(chat);

            //     if (chatObj.type == 'text') {
            //         let content = chatObj.message.content.replace(
            //             /(<([^>]+)>)/gi,
            //             ''
            //         );
            //         transcript +=
            //             `${chatObj.displayTime}: ${chatObj.name} : ${content}` +
            //             '\n';
            //     } else if (chatObj.type == 'image' || chatObj.type == 'video') {
            //         transcript +=
            //             `${chatObj.displayTime}: ${chatObj.name} : ${chatObj.message.url}` +
            //             '\n';
            //     } else if (chatObj.type == 'summary'){
            //         let content = chatObj.message.content.replace(
            //             /(<([^>]+)>)/gi,
            //             ''
            //         );
            //         transcript +=
            //             '\n' +
            //             `${t('summaryCs')}:` + '\n' + `${content}` +
            //             '\n';  
            //     }
            // });
            // console.log('Cache Summary', cache_summary);
            // if (cache_summary){
            //     let chatObj = JSON.parse(cache_summary);
            //     if (chatObj.type == 'summary'){
            //         let content = chatObj.message.content.replace(
            //             /(<([^>]+)>)/gi,
            //             ''
            //         );
            //         transcript +=
            //             '\n' +
            //             `${t('summaryCs')}:` + '\n' + `${content}` +
            //             '\n';  
            //     }
            // }
            // console.log('Transcript', transcript)
            // sendChatHistoryEvent({
            //     chatId: props.session.chatId,
            //     chatTranscript: transcript,
            //     botName: botName,
            //     conversationId: props.session.conversationId || event.data.conversationId,
            //     memberSubject: event.data.member_subject,
            //     channelAddressIdentifier: event.data.channel_address_identifier,
            //     recordId: event.data.recordId,
            //     isVipBot: isVipBot
            // });
            dispatch(editSession({ name: 'historyTranscriptSent', value: true }));
            setCacheByField(props.session.sessionId, 'historyTranscriptSent', true);
            setChatModeLocal('agent');
        }
        setAgentName(String(event.data.agentName ?? botName));
        dispatch(editStatus({ name: 'queueStatus', value: 'established' }));
        dispatch(
            editSessions({
                chatMode: 'agent',
                laName:event.data.agentName ?? botName,
            })
        );

        setCacheByField(props.session.sessionId, 'queueStatus', 'established');
        setCacheByField(props.session.sessionId, 'laName', event.data.agentName ?? botName);
        
        // pushCache(
        //     props.session.sessionId + '_chats',
        //     JSON.stringify(liveAgentConnectedMessage)
        // );
        
        pushCache(
            props.session.sessionId + '_chats',
            JSON.stringify(liveAgentJoinedMessage)
        );

        // if (props.session.sentCaseDetails == false && caseNumber && caseCategory){
        //     setTimeout(() =>sendCaseDetails(), 1000)
        // }
    
        // if (checkBetSlipValue()){
        //     dispatch(
        //         addDialog({
        //             task: 'betSlip',
        //             type: 'action',
        //             data: '',
        //         })
        //     );
        //     let slipVals = [vendor, status, betslipId, matchStartDate];
        //     dispatch(
        //         editSession({
        //             name: 'betSlipVals',
        //             value: slipVals,
        //         })
        //     );
        //     setCacheByField(props.session.sessionId, 'betSlipVals', slipVals);
        // }
        if (props.session.sentCaseDetails == false && caseNumber && caseCategory){
            setTimeout(() => sendDetails('case', true), 1000);
        } else if (props.session.sentBetSlip == false && checkBetSlipValue()){
            setTimeout(() => sendDetails('betslip', true), 1000);
        }

    }

    const handleReceiveLiveAgentMessageEvent = async (event: ReceiveEventInterface) => {
        const message = event.data.message;
        const messageContent =
            typeof message === 'string'
                ? message
                : message?.content ?? message?.text ?? message?.message ?? '';
        const messageAgentName =
            typeof message === 'object' && message !== null
                ? message.agentName
                : undefined;
        const liveAgentMessage = {
            owner: 'agent',
            type: 'text',
            name:
                messageAgentName ||
                event.data.agentName ||
                agentName ||
                props.session.laName ||
                botName,
            message: {
                content:
                    typeof messageContent === 'string' ? messageContent : '',
            },
            displayTime: format(new Date(), 'hh:mmaa'),
            createdBy: new Date().getTime(),
            conversationEntryId: event.data.conversationEntryId,
            messageId : event.data.messageId
        } as ChatInterface;
        const liveAgentMessages =
            splitChatHistoryTextMessage(liveAgentMessage);

        liveAgentMessages.forEach((chatMessage) => {
            dispatch(addChat(chatMessage));
            pushCache(
                props.session.sessionId + '_chats',
                JSON.stringify(chatMessage)
            );
        });
        dispatch(incStatus({ name: 'notificationSounds' }));
        dispatch(editSession({ name: 'conversationEntryId', value: event.data.conversationEntryId }));
        setCacheByField(props.session.sessionId, 'conversationEntryId', event.data.conversationEntryId);
    }

    const handleFileUploadEvent = async (event: ReceiveEventInterface) => {
        let imageMessage = "fileUploadSuccess"
        let content
        if (event.data.messageStatus !== 'Error Uploading Image') {
            // content = {
            //     owner: 'user',
            //     name: event.data.username,
            //     type: 'text',
            //     message: {
            //         content: t(imageMessage)
            //     },
            //     displayTime: format(new Date(), 'hh:mmaa'),
            //     createdBy: new Date().getTime(),
            // } as ChatInterface
            content = {
                owner: 'user',
                name: username,
                type: 'image',
                message: {
                    url: event.data.messageStatus,
                },
                displayTime: format(new Date(), 'hh:mmaa'),
                createdBy: new Date().getTime(),
            } as ChatInterface;
        }
        else {
            content = {
                owner: 'user',
                name: event.data.username,
                type: 'text',
                message: {
                    content: t(event.data.messageStatus)
                },
                displayTime: format(new Date(), 'hh:mmaa'),
                createdBy: new Date().getTime(),
            } as ChatInterface
        }
        pushCache(props.session.sessionId + '_chats', JSON.stringify(content));
        dispatch(addChat(content));

        dispatch(incStatus({ name: 'notificationSounds' }));
    }

    const handleReconnectSseEvent = async (event: ReceiveEventInterface) => {
        if (event.data.reconnectSseStatus){
            dispatch(editSession({ name: 'conversationId', value: event.data.conversationId }));
            dispatch(editSession({ name: 'accessToken', value: event.data.accessToken }));
            dispatch(editSession({ name: 'chatId', value: event.data.chatId }));
            dispatch(editSession({ name: 'isAuthenticated', value: event.data.isAuthenticated }));

            if (chatId) {
                sendConsumeMessageEvent({
                    chatId,
                });

                sendPingEvent(chatId);
            }
        } else {
            if (queueStatus == "queueLoading"){
                let data = customValues ? JSON.parse(customValues) : {};
                let agentAuthEnabled = String(data?.agent_auth_enabled ?? 'true').toLowerCase() === 'true';
                var state = store.getState();
                var memberCode = state.session.memberCode;
                setCacheByField(props.session.sessionId, 'chatMode', 'agent');
                dispatch(editSession({ name: 'chatMode', value: 'agent' }));
                sendConnectAgentEvent({
                    chatId: chatId,
                    isStartAgent: false,
                    isStartBot: true,
                    isVip: props.session.isVip[0],
                    triggerTime: new Date().getTime().toString(),
                    language: 'chinese',
                    screen_resolution:
                        window.screen.width + 'x' + window.screen.height,
                    userAgent: navigator.userAgent,
                    nps: props.session.nps,
                    matchStartDateTime: matchStartDate,
                    betslipId: betslipId,
                    type: 'connect',
                    username: username,
                    queue: props.session.queue,
                    sfQueueId: '',
                    sfReferrer: referrerSite? referrerSite : document.referrer,
                    connect_flag: "Gloqo Connect",
                    cacheId: props.session.botId.toString()+props.session.sessionId,
                    cacheUrl : props.session.apiPath,
                    browser: getBrowser(),
                    sfPlatform: getPlatform(),
                    acqQueueFlag: data.hasOwnProperty('acq_queue_flag') ? data.acq_queue_flag : "",
                    enQueue: enQueue,
                    acqQueueDays: data.hasOwnProperty('acq_queue_days') ? data.acq_queue_days : "",
                    caseNumber: caseNumber,
                    liveAgentFlag: JSON.parse(customValues).hasOwnProperty('live_agent_mode') ? JSON.parse(customValues).live_agent_mode : '0',
                    isAuthenticated: isAuthenticated,
                    memberCode: memberCode,
                    brandCode: brandCode,
                    marketCode: marketCode,
                    agentAuthEnabled: agentAuthEnabled,
                    isVipBot: isVipBot,
                    botName: botName,
                    isAffBot: isAffBot
                });
            }
        }
    }

    const handleLiveAgentRoutingResultEvent = async (event: ReceiveEventInterface) => {
        let data = JSON.parse(customValues);
        let sfPostUrl = data['sf_post_url'] ?? "";
        let recordId = event.data.recordId;
        
        if (recordId) {
            let sfPostChatUrl = `${sfPostUrl}?id=${recordId}`;
        
            console.log("SF POST CHAT URL", sfPostChatUrl);
        
            dispatch(
                editSession({
                    name: 'postChatUrl',
                    value: sfPostChatUrl,
                })
            );
        
            setCacheByField(props.session.sessionId, 'postChatUrl', sfPostChatUrl);
        }

        if (!isChatAlive) {
            return;
        }
         
        if (event.data.routingType == "Initial") {
            // const liveAgentConnectedMessage = {
            //     owner: 'system',
            //     name: 'Mario',
            //     type: 'text',
            //     message: {
            //         content: props.systemMessages.live_agent_connected.message,
            //     },
            //     displayTime: format(new Date(), 'hh:mmaa'),
            //     createdBy: new Date().getTime(),
            // } as ChatInterface;
            // dispatch(addChat(liveAgentConnectedMessage));
            // pushCache(
            //     props.session.sessionId + '_chats',
            //     JSON.stringify(liveAgentConnectedMessage)
            // );
        } else if (event.data.routingType == "Transfer") {
            let liveAgentName = agentName ? agentName: props.session.laName
            // const liveAgentLeaveMessage = {
            //     owner: 'system',
            //     name: 'Mario',
            //     type: 'text',
            //     message: {
            //         content: props.systemMessages.live_agent_ended.message.replace(
            //             /(\{).+?(\})/g,
            //             liveAgentName
            //         ),
            //     },
            //     displayTime: format(new Date(), 'hh:mmaa'),
            //     createdBy: new Date().getTime(),
            // } as ChatInterface;
            // dispatch(addChat(liveAgentLeaveMessage));
            // pushCache(props.session.sessionId + '_chats', JSON.stringify(liveAgentLeaveMessage));

            const liveAgentTransferredMessage = {
                owner: 'system',
                name: 'Mario',
                type: 'text',
                message: {
                    content: props.systemMessages.live_agent_transferred.message.replace(
                        /(\{).+?(\})/g,
                        liveAgentName
                    ),
                },
                displayTime: format(new Date(), 'hh:mmaa'),
                createdBy: new Date().getTime(),
            } as ChatInterface;
            dispatch(addChat(liveAgentTransferredMessage));
            pushCache(
                props.session.sessionId + '_chats',
                JSON.stringify(liveAgentTransferredMessage)
            );
        }

    }

    const handleLiveAgentQueuePositionEvent = async (event: ReceiveEventInterface) => {
        dispatch(
            editStatus({
                name: 'queueNumber',
                value: event.data.queuePosition - 1,
            })
        );
        dispatch(
            editStatus({
                name: 'queueStatus',
                value: 'queueingDialog',
            })
        );

        setCacheByField(props.session.sessionId, 'queueStatus', 'queueingDialog');
        setCacheByField(props.session.sessionId, 'queueNumber', event.data.queuePosition - 1);

    }
    
    const handleLiveAgentChatEstablishedEvent = async (
        event: ReceiveEventInterface
    ) => {
        console.log('isChatAlive', isChatAlive);
        if (!isChatAlive) {
            return;
        }
        console.log('sendChatCountsEvent ', chatModeLocal);
        if (
            historyTranscriptSent != true &&
            isStartAgent != true &&
            !historyTranscriptScheduledRef.current
        ) {
            historyTranscriptScheduledRef.current = true;
            if (!isStartBot) {
                dispatch(
                    editSession({
                        name: 'isStartBot',
                        value: true,
                    })
                );
            };
            dispatch(
                editSessions({
                    isBotToAgent: true,
                    chatCountReason: 'isBotToAgent',
                })
            );
            setCacheByField(props.session.sessionId, 'isBotToAgent', true);
            setCacheByField(props.session.sessionId, 'isStartBot', true);
            const cache_chats = await popCache(
                props.session.sessionId + '_chats'
            );

            const cache_summary = await getCacheByField(props.session.sessionId + "_summary", 'summary');

            console.log('Pop cache', cache_chats);

            let transcript = `${t('history')}:\n`;
            cache_chats.map((chat: string, i: number) => {
                let chatObj = JSON.parse(chat);

                if (chatObj.type == 'text') {
                    let content = chatObj.message.content.replace(
                        /(<([^>]+)>)/gi,
                        ''
                    );
                    transcript +=
                        `${chatObj.displayTime}: ${chatObj.name} : ${content}` +
                        '\n';
                } else if (chatObj.type == 'image' || chatObj.type == 'video') {
                    transcript +=
                        `${chatObj.displayTime}: ${chatObj.name} : ${chatObj.message.url}` +
                        '\n';
                } else if (chatObj.type == 'summary'){
                    let content = chatObj.message.content.replace(
                        /(<([^>]+)>)/gi,
                        ''
                    );
                    transcript +=
                        '\n' +
                        `${t('summaryCs')}:` + '\n' + `${content}` +
                        '\n';  
                }
            });
            if (cache_summary){
                let chatObj = JSON.parse(cache_summary);
                if (chatObj.type == 'summary'){
                    let content = chatObj.message.content.replace(
                        /(<([^>]+)>)/gi,
                        ''
                    );
                    transcript +=
                        '\n' +
                        `${t('summaryCs')}:` + '\n' + `${content}` +
                        '\n';  
                }
            }
            setTimeout(() => {
                sendChasitorMessageEvent({
                    chatId: props.session.chatId,
                    affinityToken: event.data.live_agent_session.affinityToken,
                    key: event.data.live_agent_session.key,
                    message: transcript,
                    type: 'message',
                    username: props.session.username,
                    isVipBot: isVipBot,
                    isAffBot: isAffBot
                });
            }, 5000);
            dispatch(editSession({ name: 'historyTranscriptSent', value: true }));
            setCacheByField(props.session.sessionId, 'historyTranscriptSent', true);

        }
        setAgentName(String(event.data.live_agent_info.name));
        dispatch(editStatus({ name: 'queueStatus', value: 'established' }));
        dispatch(
            editSessions({
                chatMode: 'agent',
                laAffinityToken: event.data.live_agent_session.affinityToken,
                laSessionKey: event.data.live_agent_session.key,
                laSessionId: event.data.live_agent_session.id,
                laName: event.data.live_agent_info.name,
            })
        );

        dispatch(
            editSession({
                name: 'refreshWhenQueueing',
                value: false,
            })
        );
        
        setCacheByField(props.session.sessionId, 'refreshWhenQueueing', false);
        setCacheByField(props.session.sessionId, 'queueStatus', 'established');

        // if (checkBetSlipValue()){
        //     dispatch(
        //         addDialog({
        //             task: 'betSlip',
        //             type: 'action',
        //             data: '',
        //         })
        //     );

        //     let slipVals = [vendor, status, betslipId, matchStartDate];
        //     dispatch(
        //         editSession({
        //             name: 'betSlipVals',
        //             value: slipVals,
        //         })
        //     );

        //     setCacheByField(props.session.sessionId, 'betSlipVals', slipVals);

        // }
        // for (let step = 0; step < 1000; step++) {
        //     // Runs 1000 times, with values of step 0 through 4.
        //     console.log('Duplicating cache', step);
        //     setCachesByFields([
        //         {
        //             session: props.session.sessionId,
        //             field: 'chatMode' + step.toString(),
        //             value: 'agent',
        //         },
        //         {
        //             session: props.session.sessionId,
        //             field: 'laAffinityToken' + step.toString(),
        //             value: event.data.live_agent_session.affinityToken,
        //         },
        //         {
        //             session: props.session.sessionId,
        //             field: 'laSessionKey' + step.toString(),
        //             value: event.data.live_agent_session.key,
        //         },
        //         {
        //             session: props.session.sessionId,
        //             field: 'laSessionId' + step.toString(),
        //             value: event.data.live_agent_session.id,
        //         },
        //         {
        //             session: props.session.sessionId,
        //             field: 'laName' + step.toString(),
        //             value: event.data.live_agent_info.name,
        //         },
        //     ]);
        // }

        // setCacheByField(props.session.sessionId, 'chatMode', 'agent');

        setCachesByFields([
            {
                sessionId:
                    props.session.botId.toString() + props.session.sessionId,
                field: 'chatMode',
                value: 'agent',
            },
            {
                sessionId:
                    props.session.botId.toString() + props.session.sessionId,
                field: 'laAffinityToken',
                value: event.data.live_agent_session.affinityToken,
            },
            {
                sessionId:
                    props.session.botId.toString() + props.session.sessionId,
                field: 'laSessionKey',
                value: event.data.live_agent_session.key,
            },
            {
                sessionId:
                    props.session.botId.toString() + props.session.sessionId,
                field: 'laSessionId',
                value: event.data.live_agent_session.id,
            },
            {
                sessionId:
                    props.session.botId.toString() + props.session.sessionId,
                field: 'laName',
                value: event.data.live_agent_info.name,
            },
        ]);
        
        // if (caseNumber && caseCategory){
        //     let affinityToken = event.data.live_agent_session.affinityToken
        //     let key = event.data.live_agent_session.key
        //     setTimeout(() =>sendCaseDetails(affinityToken, key), 1000)
        //     // dispatch(
        //     //     addDialog({
        //     //         task: 'caseDetails',
        //     //         type: 'action',
        //     //         data: '',
        //     //     })
        //     // );
        // }

        const liveAgentConnectedMessage = {
            owner: 'system',
            name: 'Mario',
            type: 'text',
            message: {
                content: props.systemMessages.live_agent_connected.message,
            },
            displayTime: format(new Date(), 'hh:mmaa'),
            createdBy: new Date().getTime(),
        } as ChatInterface;
        dispatch(addChat(liveAgentConnectedMessage));
        pushCache(
            props.session.sessionId + '_chats',
            JSON.stringify(liveAgentConnectedMessage)
        );
        dispatch(
            editSession({
                name: 'laId',
                value: event.data.live_agent_info.userId,
            })
        );
        const liveAgentJoinedMessage = {
            owner: 'system',
            name: 'Mario',
            type: 'text',
            message: {
                content: props.systemMessages.live_agent_joined.message.replace(
                    /(\{).+?(\})/g,
                    event.data.live_agent_info.name
                ),
            },
            displayTime: format(new Date(), 'hh:mmaa'),
            createdBy: new Date().getTime(),
        } as ChatInterface;
        dispatch(addChat(liveAgentJoinedMessage));
        pushCache(
            props.session.sessionId + '_chats',
            JSON.stringify(liveAgentJoinedMessage)
        );
    };

    const handleLiveAgentChatTransferredEvent = async (
        event: ReceiveEventInterface
    ) => {
        dispatch(
            editSessions({
                laName: event.data.live_agent_info.name,
                laId: event.data.live_agent_info.userId,
            })
        );
        const liveAgentTransferredMessage = {
            owner: 'system',
            name: 'Mario',
            type: 'text',
            message: {
                content: props.systemMessages.live_agent_transferred.message,
            },
            displayTime: format(new Date(), 'hh:mmaa'),
            createdBy: new Date().getTime(),
        } as ChatInterface;
        dispatch(addChat(liveAgentTransferredMessage));
        pushCache(
            props.session.sessionId + '_chats',
            JSON.stringify(liveAgentTransferredMessage)
        );
        const liveAgentJoinedMessage = {
            owner: 'system',
            name: 'Mario',
            type: 'text',
            message: {
                content: props.systemMessages.live_agent_joined.message.replace(
                    /(\{).+?(\})/g,
                    event.data.live_agent_info.name
                ),
            },
            displayTime: format(new Date(), 'hh:mmaa'),
            createdBy: new Date().getTime(),
        } as ChatInterface;
        dispatch(addChat(liveAgentJoinedMessage));
        pushCache(
            props.session.sessionId + '_chats',
            JSON.stringify(liveAgentJoinedMessage)
        );
    };

    const handleLiveAgentChatMessageEvent = (event: ReceiveEventInterface) => {
        if (isChatAlive) {
            const content = {
                owner: 'agent',
                type: 'text',
                name: event.data.message.name,
                message: {
                    content: event.data.message.text,
                },
                displayTime: format(new Date(), 'hh:mmaa'),
                createdBy: new Date().getTime(),
            } as ChatInterface;
            dispatch(addChat(content));
            dispatch(incStatus({ name: 'notificationSounds' }));
            pushCache(
                props.session.sessionId + '_chats',
                JSON.stringify(content)
            );
        }
    };

    const handleLiveAgentFileRequestEvent = (event: ReceiveEventInterface) => {
        if (event.data.message.type === 'Requested') {
            dispatch(
                addDialog({
                    task: 'uploadFile',
                    type: 'action',
                    data: event.data.message,
                })
            );
        } else {
            let uploadFileSysMessage = 'fileUpload' + event.data.message.type;

            const uploadFileMessage = {
                owner: 'system',
                name: 'Mario',
                type: 'text',
                message: {
                    content: t(uploadFileSysMessage),
                },
                displayTime: format(new Date(), 'hh:mmaa'),
                createdBy: new Date().getTime(),
            } as ChatInterface;
            dispatch(addChat(uploadFileMessage));
            pushCache(
                props.session.sessionId + '_chats',
                JSON.stringify(uploadFileMessage)
            );
            dispatch(removeDialog('uploadFile'));
        }
    };

    const handleLiveAgentChatEnded = (event: ReceiveEventInterface) => {
        let data = JSON.parse(customValues);
        let live_agent_mode = data['live_agent_mode'] ? data['live_agent_mode'] : '0'
        const state = store.getState();
        var isChatAlive = state.status.isChatAlive
        if (!event.data.isAgentEnd && live_agent_mode == '1'){
            if (!isVipBot && isChatAlive){
                dispatch(
                    addDialog({
                        task: 'ended',
                        type: 'action',
                    })
                );
            }
        }
        else {
            const content = {
                owner: 'system',
                name: 'Mario',
                type: 'text',
                message: {
                    content: props.systemMessages.live_agent_ended.message.replace(
                        /(\{).+?(\})/g,
                        agentName ? agentName : event.data.agentName
                    ),
                },
                displayTime: format(new Date(), 'hh:mmaa'),
                createdBy: new Date().getTime(),
            } as ChatInterface;
            dispatch(addChat(content));
            if (isChatAlive){
                pushCache(props.session.sessionId + '_chats', JSON.stringify(content));
                if (!isVipBot){
                    dispatch(
                        addDialog({
                            task: 'ended',
                            type: 'action',
                        })
                    );
                }
            }
        }
        endSession(props.session.sessionId);
    };
    
    const connectEvent = () => {
        if (socket) {
            let _socketEvent = Object.assign({}, socketEvent);
            _socketEvent.eventType = 'connect';
            _socketEvent.timestamp = new Date().getTime();
            _socketEvent.chatTs = byPassChatTs || isVipBot ? "CHAT_TIMESTAMP_BYPASS" : chatTs;
            _socketEvent.checkAgentAvailability =
                affiliateAvailabilityPending === true;
            socket.send(JSON.stringify(_socketEvent));
        }
    };

    const connectAgent = (connectVal : {isVip?: any, flag?:string}) => {
        let tempVip = connectVal.isVip.toString();
        let tempPropsVip = props.session.isVip[0].toString();
        const vipStatus = tempVip ? tempVip : tempPropsVip;
        let flagVal = connectVal.flag? connectVal.flag : "";
        let data = JSON.parse(customValues);
        let live_agent_mode = data['live_agent_mode'] ? data['live_agent_mode'] : '0'
        const state = store.getState();
        const isAuthenticated = state.session.isAuthenticated;
        const memberCode = state.session.memberCode;

         if (botIdRequireLanguageSelection.includes(props.session.botId)) {
            Swal.fire({
                text: t('Preferred Language'),
                input: 'select',
                inputOptions: {
                    en: 'English',
                    hindi: 'हिंदी',
                },
                inputPlaceholder: t('Please select your language'),
                showCancelButton: false,
                confirmButtonColor: botButtonColor,
                allowOutsideClick: false,
                confirmButtonText: t('Confirm'),
                inputValidator: (value) => {
                    return new Promise((resolve: any) => {
                        if (value) {
                            resolve();
                        } else {
                            resolve(t('Please select your preferred language'));
                        }
                    });
                },
            }).then((result) => {
                if (result.value) {
                    dispatch(
                        editStatus({ name: 'queueStatus', value: 'queueLoading' })
                    );
                    setCacheByField(props.session.sessionId, 'queueStatus', 'queueLoading');
                    let data = customValues ? JSON.parse(customValues) : {};
                    let agentAuthEnabled = String(data?.agent_auth_enabled ?? 'true').toLowerCase() === 'true';
                    sendConnectAgentEvent({
                        chatId: props.session.chatId,
                        isStartAgent: true, //check
                        isStartBot: false, //check
                        isVip: vipStatus === "true"? true: false,
                        triggerTime: new Date().getTime().toString(),
                        language: 'chinese',
                        screen_resolution:
                            window.screen.width + 'x' + window.screen.height,
                        userAgent: navigator.userAgent,
                        nps: props.session.nps,
                        matchStartDateTime: props.session.matchStartDate,
                        betslipId: props.session.betslipId,
                        type: 'connect',
                        username: props.session.username,
                        queue: props.session.queue,
                        sfQueueId: result.value ?? '',
                        sfReferrer: referrerSite? referrerSite : document.referrer,
                        connect_flag: flagVal,
                        browser: getBrowser(),
                        sfPlatform: getPlatform(),
                        cacheId: props.session.botId.toString()+props.session.sessionId,
                        cacheUrl : props.session.apiPath,
                        acqQueueFlag: data.hasOwnProperty('acq_queue_flag') ? data.acq_queue_flag : "",
                        enQueue: enQueue,
                        acqQueueDays: data.hasOwnProperty('acq_queue_days') ? data.acq_queue_days : "",
                        caseNumber: caseNumber,
                        liveAgentFlag: JSON.parse(customValues).hasOwnProperty('live_agent_mode') ? JSON.parse(customValues).live_agent_mode : '0',
                        isAuthenticated: isAuthenticated,
                        memberCode: memberCode,
                        brandCode: brandCode,
                        marketCode: marketCode,
                        agentAuthEnabled: agentAuthEnabled,
                        isVipBot: isVipBot,
                        botName: botName,
                        isAffBot: isAffBot
                    });
                }
            });
        } else {
            dispatch(editStatus({ name: 'queueStatus', value: 'queueLoading' }));
            setCacheByField(props.session.sessionId, 'queueStatus', 'queueLoading');

            let data = customValues ? JSON.parse(customValues) : {};
            let agentAuthEnabled = String(data?.agent_auth_enabled ?? 'true').toLowerCase() === 'true';
            sendConnectAgentEvent({
                chatId: props.session.chatId,
                isStartAgent: true, //check
                isStartBot: false, //check
                isVip: vipStatus === "true"? true: false,
                triggerTime: new Date().getTime().toString(),
                language: 'chinese',
                screen_resolution:
                    window.screen.width + 'x' + window.screen.height,
                userAgent: navigator.userAgent,
                nps: props.session.nps,
                matchStartDateTime: props.session.matchStartDate,
                betslipId: props.session.betslipId,
                type: 'connect',
                username: props.session.username,
                sfReferrer: referrerSite? referrerSite : document.referrer,
                queue: props.session.queue,
                connect_flag: flagVal,
                browser: getBrowser(),
                sfPlatform: getPlatform(),
                cacheId: props.session.botId.toString()+props.session.sessionId,
                cacheUrl : props.session.apiPath,
                acqQueueFlag: data.hasOwnProperty('acq_queue_flag') ? data.acq_queue_flag : "",
                enQueue: enQueue,
                acqQueueDays: data.hasOwnProperty('acq_queue_days') ? data.acq_queue_days : "",
                caseNumber: caseNumber,
                liveAgentFlag: JSON.parse(customValues).hasOwnProperty('live_agent_mode') ? JSON.parse(customValues).live_agent_mode : '0',
                isAuthenticated: isAuthenticated,
                memberCode: memberCode,
                brandCode: brandCode,
                marketCode: marketCode,
                agentAuthEnabled: agentAuthEnabled,
                isVipBot: isVipBot,
                botName: botName,
                isAffBot: isAffBot
            });
        }
    };

    const sendCheckVipEvent = async (data: SendCheckVipEventInterface) => {
        if (socket) {
            if (await shouldStopSendForDuplicateTab()) {
                return;
            }
            if (data["usernameFromUrl"] === false) {
                data["chatId"] = props.session.chatId
            }
            let _socketEvent = Object.assign({}, socketEvent);
            _socketEvent.eventType = 'checkVip';
            _socketEvent.data = data;
            console.log('sendCheckVipEvent', _socketEvent);
            socket.send(JSON.stringify(_socketEvent));
        }
    };

    const sendChatCountsEvent = (data: any) => {
        console.log('sendChatCounts function', data);
        if (socket) {
            let _socketEvent = Object.assign({}, socketEvent);
            _socketEvent.eventType = 'chatCountsSubmit';
            _socketEvent.data = data;
            console.log('sendChatCountsEvent', _socketEvent);
            socket.send(JSON.stringify(_socketEvent));
        }
    };

    const shouldStopSendForDuplicateTab = async () => {
        if (!props.session.sessionId || !props.session.tabId) {
            return false;
        }
        try {
            const ended = await checkCache(
                props.session.sessionId,
                props.session.tabId
            );
            return !!ended;
        } catch (e) {
            error(
                SocketContextProvider.name,
                'checkCache before send failed',
                e
            );
            return false;
        }
    };

    const sendMessageEvent = async (data: SendMessageEventInterface) => {
        if (socket) {
            if (await shouldStopSendForDuplicateTab()) {
                return;
            }
            let _socketEvent = Object.assign({}, socketEvent);
            _socketEvent.eventType = 'message';
            _socketEvent.data = data;
            console.log('sendMessageEvent', _socketEvent);
            await dispatch(editStatus({ name: 'isBotTyping', value: true }));
            socket.send(JSON.stringify(_socketEvent));
        }
    };

    const sendUploadChatEvent = async (data: SendUploadChatEventInterface) => {
        if (socket) {
            if (await shouldStopSendForDuplicateTab()) {
                return;
            }
            let _socketEvent = Object.assign({}, socketEvent);
            _socketEvent.eventType = 'uploadChat';
            _socketEvent.data = data;
            console.log('sendUploadChatEvent', _socketEvent);
            socket.send(JSON.stringify(_socketEvent));
        }
    };

    const sendPingEvent = (chatId: string) => {
        if (socket) {
            let _socketEvent = Object.assign({}, socketEvent);
            _socketEvent.eventType = 'ping';
            _socketEvent.data = { chatId: chatId };
            // dispatch(appendSession({ name: 'debugMessage', value: 'pi-' }));
            console.log('ping', _socketEvent, socket);
            socket.send(JSON.stringify(_socketEvent));
        }
    };

    const sendIntentEvent = async (data: SendIntentEventInterface) => {
        if (chatMode != 'agent') {
            if (socket) {
                let _socketEvent = Object.assign({}, socketEvent);
                _socketEvent.eventType = 'goToIntent';
                _socketEvent.data = data;
                console.log('sendIntentEvent', _socketEvent);
                socket.send(JSON.stringify(_socketEvent));
            }
        }

        // if (socket) {
        //     let _socketEvent = Object.assign({}, socketEvent);
        //     _socketEvent.eventType = 'goToIntent';
        //     _socketEvent.data = data;
        //     console.log('sendIntentEvent', _socketEvent);
        //     socket.send(JSON.stringify(_socketEvent));
        // }
    };

    const sendFormSubmitEvent = (data: SendFormSubmitEventInterface) => {
        if (socket) {
            let _socketEvent = Object.assign({}, socketEvent);
            _socketEvent.eventType = 'formSubmit';
            _socketEvent.data = data;
            console.log(_socketEvent);
            socket.send(JSON.stringify(_socketEvent));
        }
    };

    const sendConnectAgentEvent = (data: SendConnectAgentEventInterface) => {
        if (socket) {
            const resolvedChatId = String(store.getState().session.chatId || '').trim();
            if (!resolvedChatId) {
                console.error('connectAgent blocked: empty session.chatId!!', {
                    connect_flag: data.connect_flag,
                    isStartBot: data.isStartBot,
                });
                dispatch(editStatus({ name: 'queueStatus', value: 'failed' }));
                setCacheByField(props.session.sessionId, 'queueStatus', 'failed');
                return;
            }
            let _socketEvent = Object.assign({}, socketEvent);
            _socketEvent.eventType = 'connectAgent';
            _socketEvent.data = {
                ...data,
                chatId: resolvedChatId,
                affiliate_code: affiliateCode || '',
                isAffBot: isAffBot,
            };
            console.log(_socketEvent);
            socket.send(JSON.stringify(_socketEvent));
        }
    };

    const sendReconnectAgentEvent = (
        data: SendReconnectAgentEventInterface
    ) => {
        if (socket) {
            let _socketEvent = Object.assign({}, socketEvent);
            _socketEvent.eventType = 'reconnectAgent';
            _socketEvent.data = data;
            console.log(_socketEvent);
            socket.send(JSON.stringify(_socketEvent));
        }
    };

    const sendReconnectSseEvent = (data: SendReconectSseEventInterface) => {
        if (socket) {
            let _socketEvent = Object.assign({}, socketEvent);
            _socketEvent.eventType = 'reconnectSse';
            _socketEvent.data = data;
            console.log(_socketEvent);
            socket.send(JSON.stringify(_socketEvent));
        }
    }

    const sendVerifyReconnectionEvent = (
        data: SendVerifyReconnectionEventInterface
    ) => {
        if (socket) {
            let _socketEvent = Object.assign({}, socketEvent);
            _socketEvent.eventType = 'verifyReconnection';
            _socketEvent.data = data;
            console.log(_socketEvent);
            socket.send(JSON.stringify(_socketEvent));
        }
    };

    const sendConsumeMessageEvent = (
        data: SendConsumeMessageEventInterface
    ) => {
        if (socket) {
            let _socketEvent = Object.assign({}, socketEvent);
            _socketEvent.eventType = 'consumeMessage';
            _socketEvent.data = data;
            console.log('SendConsumeMessageEventInterface', _socketEvent);
            socket.send(JSON.stringify(_socketEvent));
        }
    };

    const sendChatHistoryEvent = async (
        data: SendChatHistoryEvent
    ) => {
        if (socket) {
            if (await shouldStopSendForDuplicateTab()) {
                return;
            }
            let _socketEvent = Object.assign({}, socketEvent);
            _socketEvent.eventType = 'sendChatHistory';
            _socketEvent.data = data;
            console.log('sendChatHistoryEvent', _socketEvent);
            socket.send(JSON.stringify(_socketEvent));
        }
    };

    const chasitorPayloadSignature = (data: SendChasitorMessageEventInterface) => {
        const filename = String((data as { filename?: string }).filename ?? '');
        return `${data.chatId}|${data.type}|${data.message ?? ''}|${filename}`;
    };

    const sendChasitorMessageEvent = async (
        data: SendChasitorMessageEventInterface
    ) => {
        if (!socket) {
            return;
        }
        const signature = chasitorPayloadSignature(data);
        if (chasitorInFlightSignatureRef.current === signature) {
            console.log('skip duplicate chasitorChatMessage', signature);
            return;
        }
        chasitorInFlightSignatureRef.current = signature;
        try {
            if (await shouldStopSendForDuplicateTab()) {
                return;
            }
            let _socketEvent = Object.assign({}, socketEvent);
            _socketEvent.eventType = 'chasitorChatMessage';
            _socketEvent.data = data;
            console.log('sendChasitorMessageEvent', _socketEvent);
            socket.send(JSON.stringify(_socketEvent));
        } finally {
            if (chasitorInFlightSignatureRef.current === signature) {
                chasitorInFlightSignatureRef.current = null;
            }
        }
    };

    const sendChasitorTypingEvent = async (
        data: SendVerifyReconnectionEventInterface
    ) => {
        if (socket) {
            if (await shouldStopSendForDuplicateTab()) {
                return;
            }
            let _socketEvent = Object.assign({}, socketEvent);
            _socketEvent.eventType = 'chasitorTyping';
            _socketEvent.data = data;
            console.log('sendchasitorTypingEvent', _socketEvent);
            socket.send(JSON.stringify(_socketEvent));
        }
    };

    const sendChasitorNotTypingEvent = async (
        data: SendVerifyReconnectionEventInterface
    ) => {
        if (socket) {
            if (await shouldStopSendForDuplicateTab()) {
                return;
            }
            let _socketEvent = Object.assign({}, socketEvent);
            _socketEvent.eventType = 'chasitorNotTyping';
            _socketEvent.data = data;
            console.log('sendchasitorNotTypingEvent', _socketEvent);
            socket.send(JSON.stringify(_socketEvent));
        }
    };

    const sendUploadFileEvent = (data: SendUploadFileEventInterface) => {
        if (socket) {
            let _socketEvent = Object.assign({}, socketEvent);
            _socketEvent.eventType = 'uploadFile';
            _socketEvent.data = data;
            console.log(_socketEvent);
            socket.send(JSON.stringify(_socketEvent));
        }
    };

    const sendChatRatingEvent = async (data: SendChatRatingInterface) => {
        if (socket) {
            if (chatMode == 'bot') {
                data['agentName'] = props.settings.botName;
            }
            let _socketEvent = Object.assign({}, socketEvent);
            _socketEvent.eventType = 'ratingSubmit';
            _socketEvent.data = data;
            console.log('CHAT RATING', _socketEvent);
            socket.send(JSON.stringify(_socketEvent));
        }
    };

    const sendChatEndEvent = async (data: SendChatEndInterface) => {
        if (socket) {
            let _socketEvent = Object.assign({}, socketEvent);
            _socketEvent.eventType = 'chasitorChatEnd';
            _socketEvent.data = data;
            console.log('chasitorChatEnd', _socketEvent);
            socket.send(JSON.stringify(_socketEvent));
        }
    };

    const sendBotChatEndEvent = async (data: SendBotChatEndInterface) => {
        if (socket) {
            let _socketEvent = Object.assign({}, socketEvent);
            _socketEvent.eventType = 'chatEnd';
            _socketEvent.data = data;
            console.log('chatEnd', _socketEvent);
            socket.send(JSON.stringify(_socketEvent));
        }
    };

    const sendFileMessageEvent = async (
        data: SendFileMessageInterface
    ) => {
        if (socket) {
            if (await shouldStopSendForDuplicateTab()) {
                return;
            }
            let _socketEvent = Object.assign({}, socketEvent);
            _socketEvent.eventType = 'fileMessage';
            _socketEvent.data = data;
            console.log('sendFileMessageEvent', _socketEvent);
            socket.send(JSON.stringify(_socketEvent));
        }
    };

    // SF Message Read Event
    const sendMessageReadEvent = async (
        data: SendMessageReadInterface
    ) => {
        if (socket) {
            if (await shouldStopSendForDuplicateTab()) {
                return;
            }
            let _socketEvent = Object.assign({}, socketEvent);
            _socketEvent.eventType = 'messageRead';
            _socketEvent.data = data;
            console.log('sendMessageReadEvent', _socketEvent);
            socket.send(JSON.stringify(_socketEvent));
        }
    };

    const sendMessageSeenEvent = async (
        data: SendMessageSeenInterface
    ) => {
        if (socket) {
            if (await shouldStopSendForDuplicateTab()) {
                return;
            }
            let _socketEvent = Object.assign({}, socketEvent);
            _socketEvent.eventType = 'messageSeen';
            _socketEvent.data = data;
            console.log('sendMessageSeenEvent', _socketEvent);
            socket.send(JSON.stringify(_socketEvent));
        }
    };

    const sendLoginEvent = async (
        data: SendLoginInterface
    ) => {
        if (socket) {
            if (await shouldStopSendForDuplicateTab()) {
                return;
            }
            let _socketEvent = Object.assign({}, socketEvent);
            _socketEvent.eventType = 'login';
            _socketEvent.data = data;
            console.log('sendLoginEvent', _socketEvent);
            socket.send(JSON.stringify(_socketEvent));
        }
    };

    // const sendAuthEvent = async (data: SendHandshakeTokenEvent) => {
    //     if (socket) {
    //         let _socketEvent = Object.assign({}, socketEvent);
    //         _socketEvent.eventType = 'authenticate';
    //         _socketEvent.data = data;
    //         console.log('authenticationData', _socketEvent);
    //         socket.send(JSON.stringify(_socketEvent));
    //     }
    // };

    // const sendCaseDetails = (sendAgentFlag: boolean = false, laAffinityToken: string = "", laSessionKey: string = "") => {
    //     let caseDetails =
    //         `${t('caseNumber')}: ${caseNumber}` +
    //         '\n' +
    //         `${t('caseCategory')}: ${caseCategory}`;

    //     const content = {
    //         owner: 'user',
    //         name: username,
    //         type: 'text',
    //         message: {
    //             content: caseDetails,
    //         },
    //         displayTime: format(new Date(), 'hh:mmaa'),
    //         createdBy: new Date().getTime(),
    //     } as ChatInterface;
    //     pushCache(props.session.sessionId + '_chats', JSON.stringify(content));
    //     dispatch(addChat(content));

    //     if (sendAgentFlag == true){
    //         let data = {
    //             affinityToken: laAffinityToken,
    //             key: laSessionKey,
    //             message: caseDetails,
    //             type: 'message',
    //             username: username,
    //             chatId: chatId,
    //             liveAgentFlag: live_agent_flag,
    //             conversationId: props.session.conversationId,
    //             accessToken: props.session.accessToken,
    //             isAuthenticated:  props.session.isAuthenticated
    //         };    
    //         if (!respondedToAgent && chatCountReason != 'respondedToAgent') {
    //             dispatch(
    //                 editSessions({
    //                     respondedToAgent: true,
    //                     chatCountReason: 'respondedToAgent',
    //                 })
    //             );
    //         }
    //         sendChasitorMessageEvent(data);
    //     } else {
    //         let cacheInfo = {
    //             sessionId: props.session.botId.toString() + props.session.sessionId,
    //             cacheUrl: props.session.apiPath,
    //             botName: botName
    //         }
    //         let data = {
    //             message: caseDetails,
    //             original_message: caseDetails,
    //             type: 'message',
    //             username: username,
    //             chatId: chatId,
    //             customer_lang_culture:
    //                 botLanguage.toLowerCase() === 'chinese'
    //                     ? 'zh-cn'
    //                     : botLanguage.toLowerCase() === 'thai'
    //                     ? 'th-th'
    //                     : botLanguage.toLowerCase() === 'vietnamese'
    //                     ? 'vi-vn'
    //                     : botLanguage.toLowerCase() === 'vietnam'
    //                     ? 'vi-vn'
    //                     : botLanguage.toLowerCase() === 'english'
    //                     ? 'en'
    //                     : botLanguage.toLowerCase(),
    //             operating_system: getOS(navigator),
    //             screen_resolution:
    //                 window.screen.width + '*' + window.screen.height,
    //             platform: 'mario',
    //             group_name: '',
    //             processor: processor,
    //             cacheInfo: cacheInfo,
    //             brandInfo : {
    //                 brand: brandCode,
    //                 market: marketCode
    //             }
    //         };
    
    //         if (!respondedToBot && chatCountReason != 'respondedToBot') {
    //             if (!isStartBot) {
    //                 dispatch(
    //                     editSession({
    //                         name: 'isStartBot',
    //                         value: true,
    //                     })
    //                 );
    //             };
    //             dispatch(
    //                 editSessions({
    //                     respondedToBot: true,
    //                     chatCountReason: 'respondedToBot',
    //                 })
    //             );
    //         }
    
    //         dispatch(editStatus({ name: 'isBotTyping', value: true }));
    //         dispatch(editSession({ name: 'waitingAnswerReply', value: true }));
    //         setCacheByField(props.session.sessionId, 'waitingAnswerReply', true);
    //         sendMessageEvent(data);
    //     }
    //     dispatch(
    //         editSession({
    //             name: 'sentCaseDetails',
    //             value: true,
    //         })
    //     );

    //     setCacheByField(props.session.sessionId, 'sentCaseDetails', true);
    // }

    // const sendBetSlipDetails = (sendAgentFlag: boolean = false, laAffinityToken: string = "", laSessionKey: string = "") => {
    //     let betSlipDetails =
    //         `${t('betSlipVendor')}: ${vendor}` +
    //         '\n' +
    //         `${t('betSlipStatus')}: ${decodeURIComponent(status)}` +
    //         '\n' +
    //         `${t('betSlipBetID')}: ${betslipId}` +
    //         '\n' +
    //         `${t('betSlipMatchDateTime')}: ${decodeURIComponent(
    //             matchStartDate
    //         )}`;


    //     const content = {
    //         owner: 'user',
    //         name: username,
    //         type: 'text',
    //         message: {
    //             content: betSlipDetails,
    //         },
    //         displayTime: format(new Date(), 'hh:mmaa'),
    //         createdBy: new Date().getTime(),
    //     } as ChatInterface;
    //     pushCache(props.session.sessionId + '_chats', JSON.stringify(content));
    //     dispatch(addChat(content));

    //     if (sendAgentFlag == true){
    //         let data = {
    //             affinityToken: laAffinityToken,
    //             key: laSessionKey,
    //             message: betSlipDetails,
    //             type: 'message',
    //             username: username,
    //             chatId: chatId,
    //             liveAgentFlag: live_agent_flag,
    //             conversationId: props.session.conversationId,
    //             accessToken: props.session.accessToken,
    //             isAuthenticated:  props.session.isAuthenticated
    //         };    
    //         if (!respondedToAgent && chatCountReason != 'respondedToAgent') {
    //             dispatch(
    //                 editSessions({
    //                     respondedToAgent: true,
    //                     chatCountReason: 'respondedToAgent',
    //                 })
    //             );
    //         }
    //         sendChasitorMessageEvent(data);
    //     } else {
    //         let cacheInfo = {
    //             sessionId: props.session.botId.toString() + props.session.sessionId,
    //             cacheUrl: props.session.apiPath,
    //             botName: botName
    //         }
    //         let data = {
    //             message: betSlipDetails,
    //             original_message: betSlipDetails,
    //             type: 'message',
    //             username: username,
    //             chatId: chatId,
    //             customer_lang_culture:
    //                 botLanguage.toLowerCase() === 'chinese'
    //                     ? 'zh-cn'
    //                     : botLanguage.toLowerCase() === 'thai'
    //                     ? 'th-th'
    //                     : botLanguage.toLowerCase() === 'vietnamese'
    //                     ? 'vi-vn'
    //                     : botLanguage.toLowerCase() === 'vietnam'
    //                     ? 'vi-vn'
    //                     : botLanguage.toLowerCase() === 'english'
    //                     ? 'en'
    //                     : botLanguage.toLowerCase(),
    //             operating_system: getOS(navigator),
    //             screen_resolution:
    //                 window.screen.width + '*' + window.screen.height,
    //             platform: 'mario',
    //             group_name: '',
    //             processor: processor,
    //             cacheInfo: cacheInfo,
    //             brandInfo : {
    //                 brand: brandCode,
    //                 market: marketCode
    //             }
    //         };

    //         if (!respondedToBot && chatCountReason != 'respondedToBot') {
    //             if (!isStartBot) {
    //                 dispatch(
    //                     editSession({
    //                         name: 'isStartBot',
    //                         value: true,
    //                     })
    //                 );
    //             };
    //             dispatch(
    //                 editSessions({
    //                     respondedToBot: true,
    //                     chatCountReason: 'respondedToBot',
    //                 })
    //             );
    //         }

    //         dispatch(editStatus({ name: 'isBotTyping', value: true }));
    //         dispatch(editSession({ name: 'waitingAnswerReply', value: true }));
    //         setCacheByField(props.session.sessionId, 'waitingAnswerReply', true);
    //         sendMessageEvent(data);
    //     }
    //     dispatch(
    //         editSession({
    //             name: 'sentBetSlip',
    //             value: true,
    //         })
    //     );

    //     setCacheByField(props.session.sessionId, 'sentBetSlip', true);
    // }

    const sendDetails = (
        detailsType: 'case' | 'betslip',
        sendAgentFlag: boolean = false,
        laAffinityToken: string = "",
        laSessionKey: string = ""
    ) => {
        let messageContent = '';
        let sessionFlagName = '';
        let cacheFieldName = '';
    
        if (detailsType === 'case') {
            messageContent = `${t('caseNumber')}: ${caseNumber}\n${t('caseCategory')}: ${caseCategory}`;
            sessionFlagName = 'sentCaseDetails';
            cacheFieldName = 'sentCaseDetails';
        } else if (detailsType === 'betslip') {
            messageContent =
                `${t('betSlipVendor')}: ${vendor}\n` +
                `${t('betSlipStatus')}: ${decodeURIComponent(status)}\n` +
                `${t('betSlipBetID')}: ${betslipId}\n` +
                `${t('betSlipMatchDateTime')}: ${decodeURIComponent(matchStartDate)}`;
            sessionFlagName = 'sentBetSlip';
            cacheFieldName = 'sentBetSlip';
        }
    
        const content: ChatInterface = {
            owner: 'user',
            name: username,
            type: 'text',
            message: { content: messageContent },
            displayTime: format(new Date(), 'hh:mmaa'),
            createdBy: new Date().getTime(),
        };
    
        pushCache(props.session.sessionId + '_chats', JSON.stringify(content));
        dispatch(addChat(content));
    
        if (sendAgentFlag) {
            const data = {
                affinityToken: laAffinityToken,
                key: laSessionKey,
                message: messageContent,
                type: 'message',
                username: username,
                chatId: chatId,
                liveAgentFlag: live_agent_flag,
                conversationId: props.session.conversationId,
                accessToken: props.session.accessToken,
                isAuthenticated: props.session.isAuthenticated,
                isVipBot: isVipBot,
                isAffBot: isAffBot
            };
    
            if (!respondedToAgent && chatCountReason !== 'respondedToAgent') {
                dispatch(
                    editSessions({
                        respondedToAgent: true,
                        chatCountReason: 'respondedToAgent',
                    })
                );
            }
            sendChasitorMessageEvent(data);
        } else {
            const cacheInfo = {
                sessionId: props.session.botId.toString() + props.session.sessionId,
                cacheUrl: props.session.apiPath,
                botName: botName,
            };
            const data = {
                message: messageContent,
                original_message: messageContent,
                type: 'message',
                username: username,
                chatId: chatId,
                customer_lang_culture:
                    botLanguage.toLowerCase() === 'chinese'
                        ? 'zh-cn'
                        : botLanguage.toLowerCase() === 'thai'
                        ? 'th-th'
                        : botLanguage.toLowerCase() === 'vietnamese' || botLanguage.toLowerCase() === 'vietnam'
                        ? 'vi-vn'
                        : botLanguage.toLowerCase() === 'english'
                        ? 'en'
                        : botLanguage.toLowerCase(),
                operating_system: getOS(navigator),
                screen_resolution: window.screen.width + '*' + window.screen.height,
                platform: 'mario',
                group_name: '',
                processor: processor,
                cacheInfo: cacheInfo,
                brandInfo: { brand: brandCode, market: marketCode },
            };
    
            if (!respondedToBot && chatCountReason !== 'respondedToBot') {
                if (!isStartBot) {
                    dispatch(editSession({ name: 'isStartBot', value: true }));
                }
                dispatch(
                    editSessions({
                        respondedToBot: true,
                        chatCountReason: 'respondedToBot',
                    })
                );
            }
    
            dispatch(editStatus({ name: 'isBotTyping', value: true }));
            dispatch(editSession({ name: 'waitingAnswerReply', value: true }));
            setCacheByField(props.session.sessionId, 'waitingAnswerReply', true);
            sendMessageEvent(data);
        }
    
        dispatch(editSession({ name: sessionFlagName, value: true }));
        setCacheByField(props.session.sessionId, cacheFieldName, true);
    };

    const onError = (err: any) => {
        error(SocketContextProvider.name, `Socket on error: ${err}`);
        dispatch(appendSession({ name: 'debugMessage', value: err }));
        dispatch(addDialog({ task: 'disconnected', type: 'action' }));
        dispatch(editStatus({ name: 'isChatAlive', value: false }));
    };

    const onClose = (e: any) => {
        info(SocketContextProvider.name, `Socket on close: ${e}`);
        dispatch(appendSession({ name: 'debugMessage', value: 'close-' }));
        dispatch(addDialog({ task: 'disconnected', type: 'action' }));
        dispatch(editStatus({ name: 'isChatAlive', value: false }));
    };

    const generator = function* (array: string[]) {
        for (let i = 0; i < array.length; i++) {
            yield array[i];
        }
    };
    const socketPathsGenerator = generator(props.socketPaths);

    useEffect(() => {
        let socketPath = socketPathsGenerator.next();
        if (!socketPath.done) {
            setPath(socketPath.value);
        }
    }, []);

    useEffect(() => {
        info(SocketContextProvider.name, 'connecting socket', path);
        try {
            let wSocket: WebSocket = new WebSocket(path);
            wSocket.onopen = function (e) {
                setSocket(wSocket);
                info(
                    SocketContextProvider.name,
                    'socket opened:',
                    socket,
                    path
                );
                dispatch(
                    editSession({
                        name: 'socketPaths',
                        value: JSON.stringify([path]),
                    })
                );

                setCacheByField(
                    props.session.sessionId,
                    'socketPaths',
                    JSON.stringify(path)
                );
            };
            wSocket.onerror = function (e) {
                error(SocketContextProvider.name, 'socket error:', e);
                dispatch(
                    appendSession({ name: 'debugMessage', value: `error- ${e}` })
                );
                let socketPath = socketPathsGenerator.next();
                if (!socketPath.done) {
                    setPath(socketPath.value);
                } else {
                    error(SocketContextProvider.name, 'No socket path to try.');
                }
            };
            wSocket.onclose = function (e) {
                dispatch(
                    appendSession({ name: 'debugMessage', value: 'closed-' })
                );
            };
        } catch (e) {
            error(SocketContextProvider.name, `Socket connecting error:`, e);
        }
    }, [path]);

    const initSocketConnection = async () => {
        if (socket != null) {
            connectEvent();
        }
    };

    useEffect(() => {
        if (socket != null) {
            info(SocketContextProvider.name, 'add listener', socket);
            socket.removeEventListener('message', onMessage);
            socket.addEventListener('open', onOpen);
            socket.addEventListener('message', onMessage);
            socket.addEventListener('error', onError);
            socket.addEventListener('close', onClose);

            return () => {
                socket.removeEventListener('message', onMessage);
            };
        } else {
            warning(SocketContextProvider.name, 'socket is not null', socket);
        }
    }, [socket, chatId, chatMode, chatModeLocal, agentName, username, isChatAlive, vip, conversationId]);

    useEffect(() => {
        if (socket != null) {
            initSocketConnection();
            dispatch(
                editStatus({
                    name: 'isLoading',
                    value: false,
                })
            );
            if (waitingAnswerReply && processor  == 2 ){
                handleWaitingAnswerReply(true, retryCacheAttemps)
            }
        } else {
            warning(SocketContextProvider.name, 'socket is not null', socket);
        }
    }, [socket]);

    useEffect(() => {
        if (socket) {
            if (
                isStartBot ||
                isStartAgent ||
                isBotToAgent ||
                respondedToBot ||
                respondedToAgent
            )
                sendChatCountsEvent({
                    chatId,
                    username: session.username,
                    isStartBot,
                    isStartAgent,
                    isBotToAgent,
                    respondedToBot,
                    respondedToAgent,
                    reason: chatCountReason,
                    datetime: new Date().toJSON(),
                });
                startSessionChecker(chatId);
        }
    }, [chatCountReason]);

    return (
        <SocketContext.Provider
            value={{
                socket,
                sendChasitorMessageEvent,
                sendChasitorTypingEvent,
                sendChasitorNotTypingEvent,
                sendChatEndEvent,
                sendChatRatingEvent,
                sendCheckVipEvent,
                sendConnectAgentEvent,
                sendConsumeMessageEvent,
                sendFormSubmitEvent,
                sendIntentEvent,
                sendMessageEvent,
                sendPingEvent,
                sendReconnectAgentEvent,
                sendUploadFileEvent,
                sendVerifyReconnectionEvent,
                sendUploadChatEvent,
                // sendAuthEvent,
                sendReconnectSseEvent,
                sendFileMessageEvent,
                sendMessageReadEvent,
                sendMessageSeenEvent,
                sendLoginEvent,
                sendBotChatEndEvent
            }}
        >
            {props.children}
        </SocketContext.Provider>
    );
};

export default SocketContextProvider;
